import Phaser from 'phaser';
import type { GameState, Side, Unit, UnitType, TerrainType, OverlayType, HexCoord, CommandCard, LogEntry, ChatMessage } from '~shared/types';
import { HexBoard, hexToPixel, drawTerrainTile } from '../objects/HexBoard';
import { UnitSprite } from '../objects/Unit';
import { CardHand } from '../objects/CardHand';
import { socketClient } from '../net/SocketClient';
import { getValidMoveHexes, getValidAttackTargets, canBattle, attackDiceCount, attackDiceBreakdown, cardMoveBonusFor } from './gameLogic';
import type { DiceBreakdown } from './gameLogic';
import { DiceRoller } from '../objects/DiceRoller';
import { drawDieFace, drawBlankDie, DIE_SIZE } from '../objects/DiceFace';
import { cardTextureKey } from './PreloadScene';
import { SCENARIO_DESCRIPTIONS } from '../data/scenarioDescriptions';
import { getTerrainSummary } from '../utils/terrainSummary';
import { getUnitRulesSummary } from '../utils/unitSummary';
import { getTerrainRule } from '~shared/terrain-rules';
import { openRulesPanel, destroyRulesPanel } from '../rulesPanel';
import { drawMedalTrack, MEDAL_TRACK_WIDTH } from '../objects/MedalTrack';

const BOARD_X = 284;  // shifted right 240px to make room for the left panel
const BOARD_Y = 95;   // row-0 top edge = 95-40 = 55px; clears the 48px header bar

// Victory medal tracks — board spans x:[250,1150], y:[55,615].
// Mine sits just below the board's bottom-left corner; opponent's sits in the
// header bar above the board's top-right corner.
const MY_MEDAL_X  = 254;
const MY_MEDAL_Y  = 618;
const OPP_MEDAL_X = 1150 - MEDAL_TRACK_WIDTH;
const OPP_MEDAL_Y = 3;

// Left panel (log + chat)
const PANEL_L_CX = 120; // centre of the 240px left panel

// Right panel (card + dice)
const PANEL_CX = 1298; // horizontal centre of the right info panel

// Bottom strip
const BTN_Y  = 716;
// ── Button layout constraint ──────────────────────────────────────────────────
// Both buttons MUST stay within the right panel (separator at x=1164, canvas edge at x=1440).
// Right panel width = 272 px.  Current allocation: 140 px (action) + 10 gap + 110 px (end turn) = 260 px.
// Do NOT widen the action button beyond ~140 px without rechecking that its left edge stays > 1168.
// Action button label uses 12 px font — the longest label ("Air Strike (N) ✈") is ~120 px at 12 px.
// If you add a longer label, shorten the text or keep font at 12 px.
const BTN1_X = 1244;   // action button centre   — left edge 1174, right edge 1314  (within panel ✓)
const BTN2_X = 1374;   // end-turn button centre  — left edge 1319, right edge 1429  (within panel ✓)

// Section accent colours (card display)
const SECTION_CLR: Record<string, number> = {
  left: 0x2244aa, center: 0x225522, right: 0xaa2222, any: 0x664400,
};

// Cards a Commissar-bound side may play directly from hand, bypassing the plan/reveal sequence.
const COMMISSAR_BYPASS_CARD_IDS = new Set(['recon-left', 'recon-center', 'recon-right', 'counter-attack']);

export class GameScene extends Phaser.Scene {
  private gameState!: GameState;
  private mySide!: Side;
  private flipped!: boolean;   // true for Axis — board rendered 180° rotated
  private roomId!: string;
  private isTournamentMatch = false;
  private playerName!: string;

  private isTouch = false;   // true on touch-only devices; affects button sizes + hover behaviour
  private headerCY = 24;     // vertical centre of the header bar (taller on touch)

  // Touch gesture state (pinch-to-zoom + single-finger pan)
  private lastPinchDist        = 0;
  private lastPinchMid         = { x: 0, y: 0 };
  private singleFingerPanStart: { x: number; y: number } | null = null;
  private singleFingerPanning  = false;
  private readonly MIN_ZOOM    = 1.0;
  private readonly MAX_ZOOM    = 5.0;
  private readonly PAN_THRESHOLD = 10; // px of movement before drag becomes a pan

  private boardOriginX = BOARD_X;
  private boardOriginY = BOARD_Y;
  private panelCX = PANEL_CX;
  // Touch-only score strip in right panel (replaces corner medal tracks)
  private myScoreText!:   Phaser.GameObjects.Text;
  private oppScoreText!:  Phaser.GameObjects.Text;
  // Touch-only card hand show/hide
  private cardStripBg?: Phaser.GameObjects.Rectangle;
  private showCardsBtnLbl!: Phaser.GameObjects.Text;
  // Touch-only chat storage (no DOM chatHistoryDiv on touch)
  private chatMsgs: { playerName: string; side: string; text: string }[] = [];
  private chatModalDiv: HTMLDivElement | null = null;

  private board!: HexBoard;
  private unitSprites = new Map<string, UnitSprite>();
  private cardHand!: CardHand;

  // ── Mobile HUD-camera split (touch only) ──────────────────────────────
  // boardLayer (rendered only by cameras.main, zoomable/pannable) holds the
  // board/units/highlights/medal tracks. hudLayer (rendered only by hudCam,
  // fixed full-screen) holds everything else — header, panels, buttons,
  // card hand, dialogs. Any new `this.add.*` call made while isTouch must end
  // up in one of these two layers (via the end-of-create() sweep, an inline
  // `layer.add(...)`, or a registration hook), or it will render on neither
  // camera correctly. Both fields stay undefined on desktop.
  private boardLayer!: Phaser.GameObjects.Container;
  private hudLayer!:   Phaser.GameObjects.Container;
  private hudCam!:     Phaser.Cameras.Scene2D.Camera;
  private leftPanelHidden = false;
  private leftPanelDomElements: Phaser.GameObjects.DOMElement[] = [];
  private leftPanelPhaserObjects: Phaser.GameObjects.GameObject[] = [];
  private resetViewBtn!: Phaser.GameObjects.Rectangle;

  // Selection state (move / battle phases)
  private selectedUnitId:  string | null = null;
  private validMoves:      HexCoord[]    = [];
  private validTargetIds:  string[]      = [];
  private pendingTargetId:    string | null    = null;
  private pendingDiceCount    = 0;
  private pendingDiceBreakdown: DiceBreakdown | null = null;
  private airPowerTargets:    string[]         = [];

  // Header / status text
  private turnText!:  Phaser.GameObjects.Text;
  private phaseText!: Phaser.GameObjects.Text;
  private deckCountImg!:  Phaser.GameObjects.Image;
  private deckCountText!: Phaser.GameObjects.Text;

  // Victory medal tracks — mine (bottom-left of board) / opponent's (top-right of board)
  private myMedalGfx!:  Phaser.GameObjects.Graphics;
  private oppMedalGfx!: Phaser.GameObjects.Graphics;
  private hintText!:     Phaser.GameObjects.Text;
  private errorText!:    Phaser.GameObjects.Text;
  private botHandBadge!: Phaser.GameObjects.Text;

  // Buttons
  private actionBtn!:      Phaser.GameObjects.Rectangle;
  private actionBtnLabel!: Phaser.GameObjects.Text;
  private endTurnBtn!:      Phaser.GameObjects.Rectangle;
  private endTurnLabel!:    Phaser.GameObjects.Text;

  private waitingOverlay!: Phaser.GameObjects.Rectangle;

  // Right panel — active card
  private cardPanelBg!:      Phaser.GameObjects.Rectangle;
  private cardPanelAccent!:  Phaser.GameObjects.Rectangle;
  private cardPanelImg!:     Phaser.GameObjects.Image;
  private cardPanelName!:    Phaser.GameObjects.Text;
  private cardPanelInfo!:    Phaser.GameObjects.Text;
  private cardPanelEmpty!:   Phaser.GameObjects.Text;
  private cardZoomImg!:      Phaser.GameObjects.Image; // hover zoom overlay for panel card

  // Right panel — Commissar token (Soviet house rule)
  private commissarTokenImg!:   Phaser.GameObjects.Image;   // circular portrait, doubles as the "view queued card" button
  private commissarTokenRing!:  Phaser.GameObjects.Arc;      // gold border ring around the portrait
  private commissarTokenBadge!: Phaser.GameObjects.Rectangle; // section-coloured corner chip = "a card is queued"
  private commissarTokenLabel!: Phaser.GameObjects.Text;
  private commissarBackdrop:    Phaser.GameObjects.Rectangle | null = null; // touch: dismiss zoom on outside tap

  // Right panel — dice results
  private diceGfx!:            Phaser.GameObjects.Graphics;
  private diceResultText!:     Phaser.GameObjects.Text;
  private diceEliminatedText!: Phaser.GameObjects.Text;
  private diceBreakdownText!:  Phaser.GameObjects.Text;
  private dicePanelTitle!:     Phaser.GameObjects.Text;

  private diceRoller!:       DiceRoller;
  private rollingAnimation      = false;
  private lastShownAttackKey    = '';
  private lastShownSpecialRoll  = '';   // detects newly-arrived TFH/Medics roll for animation
  private specialRollAnimating  = false;
  private lastShownIceAttritionKey = ''; // detects newly-arrived frozen-lake attrition roll for animation
  private iceAttritionAnimating     = false;
  private lastShownMinefieldKey    = ''; // detects newly-arrived minefield trigger roll for animation
  private minefieldTriggerAnimating = false;
  // State received while dice animation is playing; applied in the animation callback.
  private pendingState: GameState | null = null;

  // Log panel DOM + Review overlay
  private logDiv!:         HTMLDivElement;
  private reviewBtn!:      Phaser.GameObjects.Rectangle;
  private reviewBtnLabel!: Phaser.GameObjects.Text;

  private reviewActive     = false;

  // Chat
  private chatHistoryDiv!: HTMLDivElement;
  private chatInput!:      HTMLInputElement;
  private welcomeShown     = false;
  private pendingChatHistory: ChatMessage[] = [];

  // Recon draw modal
  private drawOverlay!:    Phaser.GameObjects.Rectangle;
  private drawPanel!:      Phaser.GameObjects.Rectangle;
  private drawTitle!:      Phaser.GameObjects.Text;
  private drawCardImgs:    Phaser.GameObjects.Image[]   = [];
  private drawCardBorders: Phaser.GameObjects.Rectangle[] = [];
  private drawConfirmBtn!: Phaser.GameObjects.Rectangle;
  private drawConfirmLbl!: Phaser.GameObjects.Text;
  private drawSelectedId:  string | null = null;

  // Hill 317 Recon-as-Air-Power choice modal
  private h317Overlay!:   Phaser.GameObjects.Rectangle;
  private h317Panel!:     Phaser.GameObjects.Rectangle;
  private h317Title!:     Phaser.GameObjects.Text;
  private h317Desc!:      Phaser.GameObjects.Text;
  private h317AirBtn!:    Phaser.GameObjects.Rectangle;
  private h317AirLbl!:    Phaser.GameObjects.Text;
  private h317ReconBtn!:  Phaser.GameObjects.Rectangle;
  private h317ReconLbl!:  Phaser.GameObjects.Text;

  constructor() { super({ key: 'GameScene' }); }

  init(data: { gameState: GameState; mySide: Side; roomId: string; playerName: string; chatHistory?: ChatMessage[] }): void {
    this.gameState  = data.gameState;
    this.mySide     = data.mySide;
    this.flipped    = data.mySide === 'axis';
    this.roomId     = data.roomId;
    this.playerName = data.playerName;
    this.isTournamentMatch = !!data.gameState.isTournamentMatch;
    // Delivered once by LobbyScene (captured before the scene-transition race could
    // drop the server's one-shot `chat_history` event); replayed once chatHistoryDiv exists.
    this.pendingChatHistory = data.chatHistory ?? [];
    // Reset camera in case a previous session left it zoomed in
    this.cameras?.main?.setZoom(1).setScroll(0, 0);
  }

  create(): void {
    // Phaser does not call a `shutdown()` method automatically — it must be
    // wired to the scene's `shutdown` event, otherwise socket listeners from
    // this run (e.g. `game_state`) stay registered after the scene restarts
    // and intercept events meant for the next scene/game.
    this.events.once('shutdown', this.shutdown, this);

    const W = 1440, H = 768;
    this.isTouch = this.sys.game.device.input.touch;

    // Second camera for the fixed HUD (mobile only) — see field comments above.
    if (this.isTouch) {
      this.boardLayer = this.add.container(0, 0);
      this.hudLayer   = this.add.container(0, 0);
      this.hudCam = this.cameras.add(0, 0, W, H);
      this.hudCam.setZoom(1).setScroll(0, 0);
      this.cameras.main.ignore(this.hudLayer);
      this.hudCam.ignore(this.boardLayer);
    }

    // Re-lock zoom for the game scene (we manage our own pinch-to-zoom)
    const vp = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
    if (vp) vp.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
    // On touch, fill via cameras.main's own (untransformed, screen-space) clear colour
    // instead of a GameObject — a full-canvas rect would get swept into hudLayer and,
    // since hudCam renders on top of cameras.main, would cover the whole board.
    if (this.isTouch) {
      this.cameras.main.setBackgroundColor(0x1a2d1a);
    } else {
      this.add.rectangle(W / 2, H / 2, W, H, 0x1a2d1a);
    }

    // ── Header bar (solid dark strip so board never bleeds into it) ──
    // Taller on touch — at canvas scale ~0.5x on phones, 13px/11px text is unreadable;
    // the header eats a little of the board's top margin to make room for bigger text.
    const headerH = this.isTouch ? 64 : 48;
    this.headerCY = headerH / 2;
    if (this.isTouch) this.panelCX = 1270;
    this.add.rectangle(W / 2, this.headerCY, W, headerH, 0x0d1a0d).setDepth(1);

    if (!this.isTouch) {
      {
        // Clamp the full label so its right edge never reaches the turn text.
        // Turn text is centred at W/2; longest form is ~33 chars × 7px ≈ 230px,
        // so its left edge sits at ~W/2 - 115.  Give 10px extra breathing room.
        const scenarioRaw = this.gameState.scenarioId.replace(/-/g, ' ').toUpperCase();
        const fullLabel   = `MEMOIR '44  ·  ${scenarioRaw}`;
        const rightLimit  = Math.floor(W / 2 - 125); // px, safe left edge of turn text
        const charW       = 8;                        // px per glyph, 13px bold
        const maxChars    = Math.floor((rightLimit - 250) / charW);
        const label       = fullLabel.length > maxChars
          ? fullLabel.substring(0, maxChars - 1) + '…'
          : fullLabel;
        this.add.text(250, 8, label, {
          fontSize: '13px', color: '#d4af37', fontStyle: 'bold',
        }).setDepth(2);
      }
      this.add.text(250, 28, `Playing as: ${this.mySide.toUpperCase()}`, {
        fontSize: '11px', color: this.mySide === 'allies' ? '#7799ff' : '#ff7777',
      }).setDepth(2);
    }

    // Terrain info toggle — desktop only on touch (touch uses menu overlay instead).
    // Desktop starts ON (hover tooltip enabled); mobile starts OFF (tap-to-show disabled).
    this.terrainInfoMode = !this.isTouch; // ON for desktop, OFF for mobile
    if (!this.isTouch) {
      const startColor = this.terrainInfoMode ? 0x446644 : 0x243824;
      this.terrainToggleBtn = this.add.rectangle(143, this.headerCY, 44, 30, startColor)
        .setDepth(2)
        .setInteractive({ useHandCursor: true })
        .on('pointerdown', () => this.toggleTerrainInfoMode());
      this.terrainToggleBtn
        .on('pointerover', () => this.terrainToggleBtn.setFillStyle(this.terrainInfoMode ? 0x558855 : 0x3a5a3a))
        .on('pointerout',  () => this.terrainToggleBtn.setFillStyle(this.terrainInfoMode ? 0x446644 : 0x243824));
      const lblColor = this.terrainInfoMode ? '#aaffaa' : '#aabbaa';
      this.terrainToggleLbl = this.add.text(143, this.headerCY, '⛰', { fontSize: '18px', color: lblColor })
        .setOrigin(0.5).setDepth(3);

      // Rules Arbiter button — after terrain toggle, left of the MEMOIR '44 title
      const rulesBtn = this.add.rectangle(207, this.headerCY, 75, 30, 0x243824)
        .setDepth(2)
        .setInteractive({ useHandCursor: true })
        .on('pointerdown', () => openRulesPanel(socketClient.socket));
      rulesBtn.on('pointerover', () => rulesBtn.setFillStyle(0x3a5a3a))
              .on('pointerout',  () => rulesBtn.setFillStyle(0x243824));
      this.add.text(207, this.headerCY, '📖 Rules', { fontSize: '11px', color: '#aabbaa', fontStyle: 'bold' })
        .setOrigin(0.5).setDepth(3);

      // "Info" button — sits right of the Menu button, same height, only when description exists
      if (SCENARIO_DESCRIPTIONS[this.gameState.scenarioId]) {
        const infoBtn = this.add.rectangle(90, this.headerCY, 46, 30, 0x243824)
          .setDepth(2)
          .setInteractive({ useHandCursor: true })
          .on('pointerdown', () => this.showScenarioInfo());
        infoBtn
          .on('pointerover', () => infoBtn.setFillStyle(0x3a5a3a))
          .on('pointerout',  () => infoBtn.setFillStyle(0x243824));
        this.add.text(90, this.headerCY, 'Info', { fontSize: '11px', color: '#aabbaa', fontStyle: 'bold' })
          .setOrigin(0.5).setDepth(3);
      }
    } else {
      // Touch: stub out terrainToggleBtn/Lbl so toggleTerrainInfoMode() is safe to call
      this.terrainToggleBtn = this.add.rectangle(-200, -200, 1, 1, 0x000000).setVisible(false);
      this.terrainToggleLbl = this.add.text(-200, -200, '', {}).setVisible(false);
      // Scenario name in header — same style as desktop, 20px to match turn text
      this.add.text(140, this.headerCY,
        `MEMOIR '44  ·  ${this.gameState.scenarioId.replace(/-/g, ' ').toUpperCase()}`,
        { fontSize: '20px', color: '#d4af37', fontStyle: 'bold' },
      ).setOrigin(0, 0.5).setDepth(2);
    }

    this.turnText  = this.add.text(W / 2, this.isTouch ? 12 : 8,  '', { fontSize: this.isTouch ? '20px' : '13px', color: '#ffffff' }).setOrigin(0.5, 0).setDepth(2);
    this.phaseText = this.add.text(W / 2, this.isTouch ? 36 : 28, '', { fontSize: this.isTouch ? '15px' : '11px', color: '#cccccc' }).setOrigin(0.5, 0).setDepth(2);

    // Deck count — card-back thumbnail + remaining card count, right side of header
    {
      const cardW = this.isTouch ? 24 : 20;
      const cardH = this.isTouch ? 34 : 28;
      const imgX  = W - 58;
      const txtX  = W - 32;
      this.deckCountImg  = this.add.image(imgX, this.headerCY, 'card-back')
        .setDisplaySize(cardW, cardH).setDepth(2);
      this.deckCountText = this.add.text(txtX, this.headerCY, '', {
        fontSize: this.isTouch ? '16px' : '13px', color: '#ffffff', fontStyle: 'bold',
      }).setOrigin(0, 0.5).setDepth(2);
    }

    // Victory medal tracks: mine bottom-left of the board, opponent's top-right —
    // mirrors the physical board's medal-peg corners regardless of which side
    // (allies/axis) is being played, since the board itself is what flips.
    this.myMedalGfx  = this.add.graphics().setDepth(3);
    this.oppMedalGfx = this.add.graphics().setDepth(2);
    if (this.isTouch) this.boardLayer.add([this.myMedalGfx, this.oppMedalGfx]);

    this.diceRoller = new DiceRoller(this);
    if (this.isTouch) this.diceRoller.setContainerHook(c => this.hudLayer.add(c));

    // ── Left panel (log + chat) ───────────────────────────────────────
    if (!this.isTouch) this.createLeftPanel();

    // ── Board ────────────────────────────────────────────────────────
    this.boardOriginX = this.isTouch ? 117 : BOARD_X;
    this.boardOriginY = this.isTouch ? 100 : BOARD_Y;
    this.board = new HexBoard(this, this.boardOriginX, this.boardOriginY);
    this.board.render(this.gameState.terrain, this.gameState.specialHexes, this.flipped, this.gameState.terrainTheme);
    this.board.tooltipEnabled = this.terrainInfoMode; // sync initial state
    this.board.on('hexClick', this.onHexClick, this);
    if (this.isTouch) this.board.addLayerObjectsTo(this.boardLayer);

    // Board-area dim overlay — board spans x≈250–1150, y≈55–620
    this.waitingOverlay = this.isTouch
      ? this.add.rectangle(550, 340, 1100, 480, 0x000000, 0).setDepth(5)
      : this.add.rectangle(700, 337, 900, 570, 0x000000, 0).setDepth(5);
    if (this.isTouch) this.boardLayer.add(this.waitingOverlay);

    // Touch gestures: pinch-to-zoom + two-finger pan (mobile only)
    if (this.isTouch) this.setupTouchGestures();

    // On touch, hudCam is transparent — opaque backdrops behind the right panel
    // and the hint/card-hand strip stop the zoomed board showing through them.
    if (this.isTouch) {
      const rightPanelBg = this.add.rectangle(1270, 384, 340, 768, 0x0d1a0d).setDepth(-1);
      this.cardStripBg = this.add.rectangle(550, 695, 1100, 146, 0x0d1a0d).setDepth(-1).setVisible(false);
      this.hudLayer.add([rightPanelBg, this.cardStripBg]);
    }

    // ── Right panel (card + dice) ─────────────────────────────────────
    this.createRightPanel();

    // ── Hint / error strip ───────────────────────────────────────────
    const boardMidX = this.isTouch ? this.boardOriginX + 433 : BOARD_X + 430;
    if (this.isTouch) {
      // Hint stays in right panel; error floats above the card hand strip
      this.hintText  = this.add.text(this.panelCX, 472, '', { fontSize: '13px', color: '#ffdd88', wordWrap: { width: 316 }, align: 'center' }).setOrigin(0.5, 0).setDepth(3);
      this.errorText = this.add.text(boardMidX, 610, '', { fontSize: '12px', color: '#ff6666', wordWrap: { width: 460 }, align: 'center' }).setOrigin(0.5, 0.5).setDepth(3);
    } else {
      // Hint in right panel; error above the card hand (board bottom ≈ y620, card hand top ≈ y658)
      this.hintText  = this.add.text(PANEL_CX, 470, '', { fontSize: '12px', color: '#ffdd88', wordWrap: { width: 240 }, align: 'center' }).setOrigin(0.5, 0).setDepth(3);
      this.errorText = this.add.text(boardMidX, 638, '', { fontSize: '12px', color: '#ff6666', wordWrap: { width: 480 }, align: 'center' }).setOrigin(0.5, 0.5).setDepth(3);
    }
    // Bot hand badge (single-player only) — shown near card hand area
    this.botHandBadge = this.add.text(boardMidX + 370, H - 62, '', {
      fontSize: '11px', color: '#aaa', backgroundColor: '#1e2e1e', padding: { x: 6, y: 3 },
    }).setOrigin(1, 0.5).setDepth(3).setVisible(false);

    // ── Card hand ────────────────────────────────────────────────────
    this.cardHand = new CardHand(this, boardMidX, H - 40, this.mySide);
    this.cardHand.onCardSelected((cardId) => {
      const s = this.gameState;
      if (s.phase === 'commissar-priming') {
        socketClient.socket.emit('prime_commissar', { cardId });
        return;
      }
      const isMyCommissarTurn = s.commissarSide === this.mySide && s.turnPhase === 'idle' && this.isMyTurn();
      if (isMyCommissarTurn && !COMMISSAR_BYPASS_CARD_IDS.has(cardId)) {
        socketClient.socket.emit('commissar_plan', { cardId });
      } else {
        socketClient.socket.emit('play_card', { cardId });
      }
    });
    this.cardHand.onCardInfo((card) => {
      this.board.clearHighlights();
      if (!card) return;
      for (const u of this.gameState.units) {
        if (this.isEligibleForCard(u, card)) {
          this.board.outlineHex(u.col, u.row, 0x44ddff);
        }
      }
    });
    if (this.isTouch) {
      this.cardHand.setVisible(false);
      // The near-fullscreen play-card dialog: on touch, hide/show card hand instead of left panel
      this.cardHand.onDialogVisibilityChange((visible) => {
        this.setCardHandVisible(!visible);
      });
    }

    // ── Recon draw modal ─────────────────────────────────────────────
    this.buildDrawModal();

    // ── Buttons ──────────────────────────────────────────────────────
    // Primary action button (label + action change per phase)
    const BTN_H = this.isTouch ? 54 : 42;
    const actionBtnX = this.isTouch ? this.panelCX : BTN1_X;
    const actionBtnY = this.isTouch ? 665 : BTN_Y;
    const actionBtnW = this.isTouch ? 308 : 140;
    this.actionBtn = this.add.rectangle(actionBtnX, actionBtnY, actionBtnW, BTN_H, 0x336633)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.onActionBtn());
    if (!this.isTouch) {
      this.actionBtn
        .on('pointerover', () => { if (this.actionBtn.alpha > 0.4) this.actionBtn.setFillStyle(0x44aa44); })
        .on('pointerout',  () => { if (this.actionBtn.alpha > 0.4) this.actionBtn.setFillStyle(0x336633); });
    }
    this.actionBtnLabel = this.add.text(actionBtnX, actionBtnY, '', {
      fontSize: '12px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5);

    // End Turn button
    const endTurnBtnX = this.isTouch ? this.panelCX : BTN2_X;
    const endTurnBtnY = this.isTouch ? 715 : BTN_Y;
    const endTurnBtnW = this.isTouch ? 308 : 110;
    this.endTurnBtn = this.add.rectangle(endTurnBtnX, endTurnBtnY, endTurnBtnW, BTN_H, 0x664433)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        if (!this.isMyTurn() || !this.canEndTurn()) return;
        if (this.gameState.turnPhase === 'battle') {
          // Warn if Armor Overrun is available but player is ending turn
          if (this.gameState.pendingOverrun) {
            this.showPhaseWarning(
              '⚠ Armor Overrun available',
              'Your armor may attack again (Overrun).\nEnd turn and skip the Overrun?',
              () => socketClient.socket.emit('end_turn'),
            );
            return;
          }
          // Warn if ordered units can still attack but player is ending turn
          const ready = this.countBattleReady();
          if (ready > 0) {
            this.showPhaseWarning(
              '⚠ Units can still battle',
              `${ready} ordered unit(s) still have an available attack.\nEnd turn anyway?`,
              () => socketClient.socket.emit('end_turn'),
            );
            return;
          }
        }
        socketClient.socket.emit('end_turn');
      });
    if (!this.isTouch) {
      this.endTurnBtn
        .on('pointerover', () => { if (this.endTurnBtn.alpha > 0.4) this.endTurnBtn.setFillStyle(0x996644); })
        .on('pointerout',  () => { if (this.endTurnBtn.alpha > 0.4) this.endTurnBtn.setFillStyle(0x664433); });
    }
    this.endTurnLabel = this.add.text(endTurnBtnX, endTurnBtnY, 'End Turn', {
      fontSize: '13px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5);

    // ── Menu button (top-left header) ────────────────────────────────
    this.createMenuButton();

    // ── Initial render ────────────────────────────────────────────────
    this.renderUnits();
    this.updateUI();

    // ── HUD camera sweep (mobile only) ──────────────────────────────────
    // Everything created above that wasn't explicitly parented into boardLayer
    // (board, units, medal tracks, waiting overlay, left-panel objects) is HUD
    // content — sweep all remaining top-level objects into hudLayer in one go.
    // DOM elements are left at the scene root (see leftPanelDomElements comment).
    if (this.isTouch) {
      this.createResetViewButton();
      const remaining = this.children.list.filter(o =>
        o !== this.boardLayer && o !== this.hudLayer &&
        !(o instanceof Phaser.GameObjects.DOMElement),
      );
      this.hudLayer.add(remaining);
    }

    // ── Welcome message ───────────────────────────────────────────────
    if (this.gameState.phase !== 'waiting') {
      this.time.delayedCall(1200, () => this.showWelcomeMessage());
    }

    // ── Socket events ─────────────────────────────────────────────────
    socketClient.socket.on('game_state', (state) => {
      const attack    = state.lastAttack;
      // Include targetId so two air-power targets with identical faces are treated as distinct attacks
      const attackKey = attack
        ? `${attack.attackerId}-${attack.targetId}-${attack.faces.join('')}-${attack.diceRolled}`
        : '';
      const isNewAttack = attackKey !== '' && attackKey !== this.lastShownAttackKey;

      // Detect a freshly-arrived special roll (TFH or Medics) that needs animation.
      // For TFH: activeCard is still set (roll happens at playCard time).
      // For Medics: activeCard is still set during pendingMedicsAdvance (advance is deferred).
      const specialRollKey = state.lastSpecialRoll ? state.lastSpecialRoll.join(',') : '';
      const specialAction  = state.activeCard?.effects?.specialAction;
      const isNewSpecialRoll =
        specialRollKey !== '' &&
        specialRollKey !== this.lastShownSpecialRoll &&
        (specialAction === 'their-finest-hour' || specialAction === 'medics' || state.pendingMedicsAdvance);

      // Detect a freshly-arrived frozen-lake attrition roll (move/retreat/take-ground onto ice).
      const ice    = state.lastIceAttrition;
      const iceKey = ice ? `${ice.unitId}-${ice.dice.join('')}-${ice.figuresLost}-${ice.eliminated}` : '';
      const isNewIceAttrition = iceKey !== '' && iceKey !== this.lastShownIceAttritionKey;

      // Detect a freshly-arrived minefield trigger (move/take-ground onto a mine).
      const mine    = state.lastMinefieldTrigger;
      const mineKey = mine ? `${mine.unitId}-${mine.dice.join('')}-${mine.figuresLost}-${mine.eliminated}-${mine.wasDecoy}` : '';
      const isNewMinefieldTrigger = mineKey !== '' && mineKey !== this.lastShownMinefieldKey;

      const terrainChanged = JSON.stringify(state.terrain) !== JSON.stringify(this.gameState.terrain);

      if (isNewAttack && attack) {
        // ── Log holdback invariant ─────────────────────────────────────────
        // oldLog is the log BEFORE this attack's entry.  We assign it to
        // this.gameState so updateLogPanel() never reveals the result while
        // the dice are still rolling.  The full log is only shown in
        // applyNewState() inside the animation callback.
        //
        // For Air Power sequential rolls this is guaranteed because
        // advance_air_strike is emitted only AFTER the animation callback
        // fires → the server cannot generate target N+1's state until
        // target N's animation is complete.  The one exception is when the
        // defender resolves a retreat DURING an animation; handled below.
        const oldUnits          = this.gameState.units;
        const oldPendingRetreat = this.gameState.pendingRetreat;
        const oldLog            = this.gameState.log;
        const oldAlliesMedals   = this.gameState.alliesMedals;
        const oldAxisMedals     = this.gameState.axisMedals;
        const oldSpecialHexes   = this.gameState.specialHexes;
        const oldWinner         = this.gameState.winner;
        this.gameState    = {
          ...state,
          units: oldUnits,
          pendingRetreat: oldPendingRetreat,
          log: oldLog,
          alliesMedals: oldAlliesMedals,
          axisMedals: oldAxisMedals,
          specialHexes: oldSpecialHexes,
          winner: oldWinner,
        };
        this.pendingState = null;
        // diceRoller.roll() calls clear() which kills any in-progress ice tweens.
        // The ice callback will never fire, so reset the flag here to prevent it getting stuck.
        if (this.iceAttritionAnimating) this.iceAttritionAnimating = false;
        if (this.minefieldTriggerAnimating) this.minefieldTriggerAnimating = false;
        if (terrainChanged) this.board.render(state.terrain, state.specialHexes, this.flipped, state.terrainTheme);
        this.renderUnits();
        this.clearSelection();
        this.cardHand.clearInfo();
        this.refreshBoardHighlights();

        // Highlight attacker → target while dice roll (both players see this)
        const attUnit = this.gameState.units.find(u => u.id === attack.attackerId);
        const tgtUnit = this.gameState.units.find(u => u.id === attack.targetId);
        if (attUnit) this.board.outlineHex(attUnit.col, attUnit.row, 0xffff00);
        if (tgtUnit) this.board.outlineHex(tgtUnit.col, tgtUnit.row, 0xff2222);

        this.lastShownAttackKey = attackKey;
        this.rollingAnimation   = true;
        this.updateUI();
        const positions = this.dicePositions(attack.diceRolled);
        this.diceRoller.roll(attack.faces, positions, () => {
          this.rollingAnimation = false;
          const pending = this.pendingState;
          this.pendingState = null;

          // Edge case: defender resolved a retreat during our animation, which
          // caused the server to auto-advance to the next Air Power target.
          // That state arrived as pendingState and has a NEW lastAttack key.
          // We must NOT skip its animation — apply the current state first,
          // then re-queue the pending state for its own animation pass.
          const pendingKey = pending?.lastAttack
            ? `${pending.lastAttack.attackerId}-${pending.lastAttack.targetId}-${pending.lastAttack.faces.join('')}-${pending.lastAttack.diceRolled}`
            : '';
          const pendingHasNewAttack = pendingKey !== '' && pendingKey !== this.lastShownAttackKey;

          if (pendingHasNewAttack && pending) {
            // Apply the just-completed target's state (correct log up to here)
            this.gameState = state;
            this.applyNewState(state, terrainChanged);
            // Re-enter the handler for the next target — simulate arrival of pending state.
            // Guard: if a newer state arrives (and is applied directly) during the 120ms window,
            // do NOT re-apply the older pending state over it.
            this.time.delayedCall(120, () => {
              if (this.gameState !== state) return; // a newer state was applied — leave it alone
              this.gameState = pending; // brief pause so player can see current result first
              this.applyNewState(pending, JSON.stringify(pending.terrain) !== JSON.stringify(state.terrain));
            });
          } else {
            // Normal path
            const finalState     = pending ?? state;
            const terrainDiffers = pending !== null &&
              JSON.stringify(pending.terrain) !== JSON.stringify(state.terrain);
            this.gameState = finalState;
            this.applyNewState(finalState, terrainDiffers);
            // Air Strike: player presses "Next Target ▶" to see each roll — no auto-advance here
          }
        });
      } else if (isNewSpecialRoll && state.lastSpecialRoll) {
        // Special roll arrived (TFH dice or Medics heal dice) — animate in the dice panel
        // Hold back units (and medals) until animation completes so restored figures
        // are not revealed before the dice result is shown.
        this.lastShownSpecialRoll = specialRollKey;
        const oldUnitsS          = this.gameState.units;
        const oldAlliesMedalsS   = this.gameState.alliesMedals;
        const oldAxisMedalsS     = this.gameState.axisMedals;
        const oldSpecialHexesS   = this.gameState.specialHexes;
        const oldWinnerS         = this.gameState.winner;
        this.gameState = {
          ...state,
          units: oldUnitsS,
          alliesMedals: oldAlliesMedalsS,
          axisMedals: oldAxisMedalsS,
          specialHexes: oldSpecialHexesS,
          winner: oldWinnerS,
        };
        if (terrainChanged) this.board.render(state.terrain, state.specialHexes, this.flipped, state.terrainTheme);
        this.renderUnits();
        this.clearSelection();
        this.refreshBoardHighlights();
        this.specialRollAnimating = true;
        this.updateUI();
        const positions = this.dicePositions(state.lastSpecialRoll.length);
        this.diceRoller.roll(state.lastSpecialRoll, positions, () => {
          this.specialRollAnimating = false;
          this.gameState = state;
          this.renderUnits();
          this.updateUI();
          // Medics: animation done — advance the turn (2 s pause so both players see the result)
          if (state.pendingMedicsAdvance && this.isMyTurn()) {
            this.time.delayedCall(2000, () => {
              if (this.gameState.pendingMedicsAdvance) {
                socketClient.socket.emit('advance_medics');
              }
            });
          }
        });
      } else if (isNewIceAttrition && ice) {
        // Frozen-lake attrition roll arrived (crossed via move/retreat/take-ground) — animate in the dice panel
        this.lastShownIceAttritionKey = iceKey;
        this.gameState = state;
        if (terrainChanged) this.board.render(state.terrain, state.specialHexes, this.flipped, state.terrainTheme);
        this.renderUnits();
        this.clearSelection();
        this.refreshBoardHighlights();
        this.iceAttritionAnimating = true;
        this.updateUI();
        const positions = this.dicePositions(ice.dice.length);
        this.diceRoller.roll(ice.dice, positions, () => {
          this.iceAttritionAnimating = false;
          // Apply any state buffered during the ice animation (mirrors the attack animation callback).
          // Skip if a dice animation is concurrently finishing — it will handle pendingState itself.
          if (!this.rollingAnimation) {
            const pending = this.pendingState;
            this.pendingState = null;
            if (pending) {
              const terrainDiffers = JSON.stringify(pending.terrain) !== JSON.stringify(this.gameState.terrain);
              this.gameState = pending;
              this.applyNewState(pending, terrainDiffers);
            } else {
              this.updateUI();
            }
          } else {
            this.updateUI();
          }
        });
      } else if (isNewMinefieldTrigger && mine) {
        // Minefield trigger arrived (move/take-ground onto a mine) — animate in the dice panel.
        // A decoy has 0 dice; DiceRoller.roll() completes immediately in that case.
        this.lastShownMinefieldKey = mineKey;
        this.gameState = state;
        if (terrainChanged) this.board.render(state.terrain, state.specialHexes, this.flipped, state.terrainTheme);
        this.renderUnits();
        this.clearSelection();
        this.refreshBoardHighlights();
        this.minefieldTriggerAnimating = true;
        this.updateUI();
        const positions = this.dicePositions(mine.dice.length);
        this.diceRoller.roll(mine.dice, positions, () => {
          this.minefieldTriggerAnimating = false;
          // Apply any state buffered during the animation (mirrors the ice-attrition callback).
          if (!this.rollingAnimation && !this.iceAttritionAnimating) {
            const pending = this.pendingState;
            this.pendingState = null;
            if (pending) {
              const terrainDiffers = JSON.stringify(pending.terrain) !== JSON.stringify(this.gameState.terrain);
              this.gameState = pending;
              this.applyNewState(pending, terrainDiffers);
            } else {
              this.updateUI();
            }
          } else {
            this.updateUI();
          }
        });
      } else if (this.rollingAnimation || this.iceAttritionAnimating || this.minefieldTriggerAnimating) {
        // A non-attack state arrived while animation is playing — defer full apply until callback.
        this.pendingState = state;
        // Terrain changes are purely visual; safe to apply immediately.
        if (terrainChanged) this.board.render(state.terrain, state.specialHexes, this.flipped, state.terrainTheme);
      } else {
        this.gameState = state;
        this.applyNewState(state, terrainChanged);
      }
    });

    socketClient.socket.on('chat_received', ({ playerName, side, text }) => {
      this.appendChatMessage(playerName, side, text);
    });

    socketClient.socket.on('game_ended', () => {
      localStorage.removeItem('memoir44_rejoin');
      // Full reload (rather than scene.start) guarantees a clean slate —
      // stale socket listeners and Phaser scene state from this game can't
      // leak into the next one created from the lobby.
      window.location.reload();
    });

    socketClient.socket.on('error', (msg) => {
      this.errorText.setText(`⚠  ${msg}`);
      this.time.delayedCall(4000, () => this.errorText.setText(''));
    });

    // Reconnect: re-join the room so the server re-associates this socket
    socketClient.socket.on('connect', () => {
      socketClient.socket.emit('join_room', { roomId: this.roomId, playerName: this.playerName });
    });

    // Handle game_start sent during reconnect or if another player joins late
    socketClient.socket.on('game_start', (state) => {
      this.gameState = state;
      this.applyNewState(state, true);  // always re-render board on game start/reconnect
    });

    // Update mySide in case the reconnect assigned a fresh side
    socketClient.socket.on('player_joined', ({ side }) => {
      socketClient.mySide = side;
      this.mySide  = side;
      this.flipped = side === 'axis';
    });
  }

  // ── Recon draw modal ───────────────────────────────────────────────

  private buildDrawModal(): void {
    const D = 40;  // depth
    this.drawOverlay = this.add.rectangle(720, 384, 1440, 768, 0x000000, 0.65)
      .setDepth(D).setVisible(false).setInteractive();

    this.drawPanel = this.add.rectangle(720, 384, 440, 300, 0x0a1a0a)
      .setStrokeStyle(2, 0xd4af37, 1).setDepth(D + 1).setVisible(false);

    this.drawTitle = this.add.text(720, 250, 'RECON — KEEP 1 CARD', {
      fontSize: '14px', color: '#d4af37', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(D + 2).setVisible(false);

    // 2 card slots (populated in showDrawModal)
    for (let i = 0; i < 2; i++) {
      const cx = 720 + (i === 0 ? -100 : 100);
      const border = this.add.rectangle(cx, 384, 124, 192, 0x000000, 0)
        .setStrokeStyle(3, 0xd4af37, 0).setDepth(D + 2).setVisible(false);
      const img = this.add.image(cx, 384, 'card-back')
        .setDisplaySize(120, 186).setDepth(D + 3).setVisible(false)
        .setInteractive({ useHandCursor: true });
      this.drawCardBorders.push(border);
      this.drawCardImgs.push(img);
    }

    this.drawConfirmBtn = this.add.rectangle(720, 504, 160, 36, 0x1a3a22)
      .setStrokeStyle(1, 0x55bb77, 1).setDepth(D + 2).setVisible(false)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => { if (this.drawSelectedId) this.drawConfirmBtn.setFillStyle(0x2a5a33); })
      .on('pointerout',  () => this.drawConfirmBtn.setFillStyle(0x1a3a22))
      .on('pointerdown', () => {
        if (!this.drawSelectedId) return;
        socketClient.socket.emit('keep_card', { cardId: this.drawSelectedId });
        this.hideDrawModal();
      });

    this.drawConfirmLbl = this.add.text(720, 504, 'Keep Card', {
      fontSize: '13px', color: '#aaffbb', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(D + 3).setVisible(false);

    // ── Hill 317 choice modal ────────────────────────────────────────────
    this.h317Overlay = this.add.rectangle(720, 384, 1440, 768, 0x000000, 0.65)
      .setDepth(D).setVisible(false).setInteractive();

    this.h317Panel = this.add.rectangle(720, 384, 460, 260, 0x0a1a0a)
      .setStrokeStyle(2, 0xd4af37, 1).setDepth(D + 1).setVisible(false);

    this.h317Title = this.add.text(720, 280, 'HILL 317 — RECON CARD', {
      fontSize: '13px', color: '#d4af37', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(D + 2).setVisible(false);

    this.h317Desc = this.add.text(720, 312, '', {
      fontSize: '11px', color: '#cccccc', wordWrap: { width: 400 }, align: 'center',
    }).setOrigin(0.5).setDepth(D + 2).setVisible(false);

    // Air Power Strike button
    this.h317AirBtn = this.add.rectangle(720, 390, 340, 52, 0x1a2a3a)
      .setStrokeStyle(1, 0x4499ff, 1).setDepth(D + 2).setVisible(false)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => this.h317AirBtn.setFillStyle(0x2a3a4a))
      .on('pointerout',  () => this.h317AirBtn.setFillStyle(0x1a2a3a))
      .on('pointerdown', () => {
        socketClient.socket.emit('resolve_hill317_choice', { choice: 'air-power' });
        this.hideHill317Modal();
      });

    this.h317AirLbl = this.add.text(720, 390,
      'Air Power Strike\n(2 dice/hex · up to 4 adjacent · section-restricted · no card draw)',
      { fontSize: '11px', color: '#88ccff', align: 'center', lineSpacing: 4 },
    ).setOrigin(0.5).setDepth(D + 3).setVisible(false);

    // Standard Recon button
    this.h317ReconBtn = this.add.rectangle(720, 460, 340, 44, 0x1a2a1a)
      .setStrokeStyle(1, 0x55bb77, 1).setDepth(D + 2).setVisible(false)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => this.h317ReconBtn.setFillStyle(0x2a3a2a))
      .on('pointerout',  () => this.h317ReconBtn.setFillStyle(0x1a2a1a))
      .on('pointerdown', () => {
        socketClient.socket.emit('resolve_hill317_choice', { choice: 'recon' });
        this.hideHill317Modal();
      });

    this.h317ReconLbl = this.add.text(720, 460,
      'Standard Recon\n(order 1 unit · draw 2 keep 1)',
      { fontSize: '11px', color: '#aaffbb', align: 'center', lineSpacing: 4 },
    ).setOrigin(0.5).setDepth(D + 3).setVisible(false);
  }

  private showHill317Modal(section: string): void {
    const label = section.charAt(0).toUpperCase() + section.slice(1);
    this.h317Desc.setText(`An Allied unit holds Hill 317. Play this ${label} Recon card as a ${label} section Air Power strike, or use it as a standard Recon card.`);
    this.h317Overlay.setVisible(true);
    this.h317Panel.setVisible(true);
    this.h317Title.setVisible(true);
    this.h317Desc.setVisible(true);
    this.h317AirBtn.setVisible(true);
    this.h317AirLbl.setVisible(true);
    this.h317ReconBtn.setVisible(true);
    this.h317ReconLbl.setVisible(true);
  }

  private hideHill317Modal(): void {
    this.h317Overlay.setVisible(false);
    this.h317Panel.setVisible(false);
    this.h317Title.setVisible(false);
    this.h317Desc.setVisible(false);
    this.h317AirBtn.setVisible(false);
    this.h317AirLbl.setVisible(false);
    this.h317ReconBtn.setVisible(false);
    this.h317ReconLbl.setVisible(false);
  }

  private showDrawModal(cards: import('~shared/types').CommandCard[]): void {
    this.drawSelectedId = null;
    this.drawOverlay.setVisible(true);
    this.drawPanel.setVisible(true);
    this.drawTitle.setVisible(true);
    this.drawConfirmBtn.setVisible(true);
    this.drawConfirmLbl.setVisible(true);

    cards.forEach((card, i) => {
      const img    = this.drawCardImgs[i];
      const border = this.drawCardBorders[i];
      img.setTexture(cardTextureKey(card.id, this.mySide)).setVisible(true);
      border.setStrokeStyle(3, 0xd4af37, 0).setVisible(true);

      img.off('pointerdown');
      img.on('pointerdown', () => {
        this.drawSelectedId = card.id;
        this.drawCardBorders.forEach((b, j) =>
          b.setStrokeStyle(3, j === i ? 0x55ff88 : 0xd4af37, j === i ? 1 : 0),
        );
        this.drawConfirmBtn.setFillStyle(0x1a3a22);
      });
    });
  }

  private hideDrawModal(): void {
    this.drawSelectedId = null;
    this.drawOverlay.setVisible(false);
    this.drawPanel.setVisible(false);
    this.drawTitle.setVisible(false);
    this.drawConfirmBtn.setVisible(false).setFillStyle(0x1a3a22);
    this.drawConfirmLbl.setVisible(false);
    this.drawCardImgs.forEach(img => img.setVisible(false));
    this.drawCardBorders.forEach(b => b.setVisible(false).setStrokeStyle(3, 0xd4af37, 0));
  }

  shutdown(): void {
    const s = socketClient.socket;
    s.off('game_state');
    s.off('chat_received');
    s.off('game_ended');
    s.off('error');
    s.off('connect');
    s.off('game_start');
    s.off('player_joined');
    destroyRulesPanel(s);
  }

  // ── Helpers ───────────────────────────────────────────────────────────

  // ── Touch gestures (mobile only) ─────────────────────────────────────

  private setupTouchGestures(): void {
    const cam = this.cameras.main;
    cam.setBounds(0, 0, 1440, 768);
    // Tell Phaser to track a second touch pointer (pointer2).
    // Without this call pointer2 is never populated and pinch cannot work.
    this.input.addPointer(2);
  }

  // Called every frame by Phaser when the scene is active.
  update(): void {
    if (this.isTouch) {
      this.updateTouchGesture();

      const cam = this.cameras.main;
      const zoomed = cam.zoom > 1.001 || Math.abs(cam.scrollX) > 0.5 || Math.abs(cam.scrollY) > 0.5;
      if (zoomed !== this.leftPanelHidden) {
        this.leftPanelHidden = zoomed;
        this.setLeftPanelVisible(!zoomed);
      }
    }
  }

  /** Show/hide the left panel (Game Log/Chat) — used while the board camera
   *  is zoomed/panned (mobile), since DOM elements can't be pinned to hudCam. */
  private setLeftPanelVisible(visible: boolean): void {
    this.leftPanelDomElements.forEach(el => el.setVisible(visible));
    this.leftPanelPhaserObjects.forEach(o => (o as Phaser.GameObjects.Text | Phaser.GameObjects.Rectangle).setVisible(visible));
  }

  private updateTouchGesture(): void {
    const p1 = this.input.pointer1;
    const p2 = this.input.pointer2;

    // ── Two-finger: pinch-to-zoom ────────────────────────────────────────
    if (p1.isDown && p2.isDown) {
      const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);

      // Guard: if the two pointers are at nearly the same position, p2 is almost
      // certainly a ghost touch (missed touchend). Treat as single finger.
      if (dist < 10) {
        this.lastPinchDist = 0; // reset so we don't mis-zoom when p2 clears
        // fall through to single-finger handling below
      } else {
        // Genuine two-finger gesture — reset single-finger state
        this.singleFingerPanning  = false;
        this.singleFingerPanStart = null;
        this.board.gestureActive  = true;

        const midX = (p1.x + p2.x) / 2;
        const midY = (p1.y + p2.y) / 2;

        if (this.lastPinchDist > 0) {
          const cam   = this.cameras.main;
          const ratio = dist / this.lastPinchDist;
          // Guard: only zoom if the distance changed meaningfully (>0.5%).
          // Filters micro-movements that shouldn't register as zoom.
          if (Math.abs(ratio - 1) > 0.005) {
            cam.setZoom(Phaser.Math.Clamp(cam.zoom * ratio, this.MIN_ZOOM, this.MAX_ZOOM));
          }
          cam.scrollX += (this.lastPinchMid.x - midX) / cam.zoom;
          cam.scrollY += (this.lastPinchMid.y - midY) / cam.zoom;
        }

        this.lastPinchDist = dist;
        this.lastPinchMid  = { x: midX, y: midY };
        return;
      }
    }

    // Reset two-finger state once second finger lifts
    this.lastPinchDist = 0;

    // ── No fingers ───────────────────────────────────────────────────────
    if (!p1.isDown) {
      if (this.singleFingerPanning) {
        this.singleFingerPanning = false;
        this.time.delayedCall(80, () => { this.board.gestureActive = false; });
      } else if (this.board.gestureActive) {
        // Pinch gesture just ended — gestureActive was never cleared (singleFingerPanning
        // is false during pinch, so the branch above never ran). Clear it now.
        this.time.delayedCall(80, () => { this.board.gestureActive = false; });
      }
      this.singleFingerPanStart = null;
      this.lastPinchMid = { x: 0, y: 0 };
      return;
    }

    // ── Single finger ────────────────────────────────────────────────────
    if (!this.singleFingerPanStart) {
      // Finger just placed — record start position for threshold check
      this.singleFingerPanStart = { x: p1.x, y: p1.y };
      this.lastPinchMid = { x: p1.x, y: p1.y };
      return;
    }

    const totalMoved = Math.hypot(
      p1.x - this.singleFingerPanStart.x,
      p1.y - this.singleFingerPanStart.y,
    );

    // Activate panning once threshold exceeded
    if (!this.singleFingerPanning && totalMoved > this.PAN_THRESHOLD) {
      this.singleFingerPanning = true;
      this.board.gestureActive = true;
      // Do NOT clearSelection() here — the player may have deliberately selected a
      // unit/target before panning to reach a button (e.g. Roll Dice).
    }

    if (this.singleFingerPanning) {
      const cam = this.cameras.main;
      cam.scrollX += (this.lastPinchMid.x - p1.x) / cam.zoom;
      cam.scrollY += (this.lastPinchMid.y - p1.y) / cam.zoom;
    }

    this.lastPinchMid = { x: p1.x, y: p1.y };
  }

  private isMyTurn(): boolean {
    return this.gameState.phase === 'playing' && this.gameState.currentSide === this.mySide;
  }

  private specialAttackAction(): 'barrage' | 'air-power' | null {
    const fx = this.gameState.activeCard?.effects;
    if (fx?.specialAction === 'barrage')   return 'barrage';
    if (fx?.specialAction === 'air-power') return 'air-power';
    return null;
  }

  private artilleryBombardMaxAttacks(unit: Unit): number {
    return this.gameState.activeCard?.effects?.specialAction === 'artillery-bombard'
      && unit.type === 'artillery' && unit.hexesMoved === 0 ? 2 : 1;
  }

  private artilleryBombardMoveRange(unit: Unit): number | undefined {
    const sa = this.gameState.activeCard?.effects?.specialAction;
    if (sa === 'artillery-bombard' && unit.type === 'artillery') return 3;
    if (sa === 'infantry-assault'  && unit.type === 'infantry')  return 3;
    return undefined;
  }

  private belMoveParams(unit: Unit): { maxDist?: number; ignoreRestrictions: boolean } {
    const isBel = this.gameState.activeCard?.effects?.specialAction === 'behind-enemy-lines'
      && unit.type === 'infantry';
    return isBel ? { maxDist: 3, ignoreRestrictions: true } : { ignoreRestrictions: false };
  }

  private isMyAirdrop(): boolean {
    return this.gameState.phase === 'airdrop' && this.gameState.pendingAirdrop?.side === this.mySide;
  }

  private canEndTurn(): boolean {
    if (this.gameState.pendingDraw)           return false;
    if (this.gameState.pendingHill317Choice)  return false;
    if (this.gameState.pendingTakeGround)     return false;
    if (this.gameState.pendingMedicsRoll)     return false;
    const p = this.gameState.turnPhase;
    return p === 'select' || p === 'move' || p === 'battle';
  }

  private getUnitSection(col: number): 'left' | 'center' | 'right' {
    if (this.mySide === 'allies') {
      if (col <= 3) return 'left';
      if (col <= 8) return 'center';
      return 'right';
    } else {
      if (col >= 9) return 'left';
      if (col >= 4) return 'center';
      return 'right';
    }
  }

  // Odd-row col 3 and col 8 sit on a section border → belong to both adjacent sections.
  private getUnitSections(col: number, row: number): ('left' | 'center' | 'right')[] {
    const primary = this.getUnitSection(col);
    if (row % 2 === 0) return [primary];
    if (this.mySide === 'allies') {
      if (col === 3) return ['left', 'center'];
      if (col === 8) return ['center', 'right'];
    } else {
      if (col === 8) return ['left', 'center'];
      if (col === 3) return ['center', 'right'];
    }
    return [primary];
  }

  /** True if a unit would be eligible to order with the given card. */
  private isEligibleForCard(unit: Unit, card: CommandCard): boolean {
    if (unit.side !== this.mySide) return false;

    const fx = card.effects;

    // Firefight: any unit NOT adjacent to an enemy
    if (fx?.specialAction === 'firefight') {
      const enemies = this.gameState.units.filter(u => u.side !== this.mySide);
      return !enemies.some(e => {
        const ax = unit.col - (unit.row - (unit.row & 1)) / 2;
        const bx = e.col   - (e.row   - (e.row   & 1)) / 2;
        return Math.max(Math.abs(ax - bx), Math.abs((-ax - unit.row) - (-bx - e.row)), Math.abs(unit.row - e.row)) === 1;
      });
    }

    // Close Assault: infantry/armor only, must be adjacent to an enemy
    if (fx?.specialAction === 'close-assault') {
      if (unit.type === 'artillery') return false;
      const enemies = this.gameState.units.filter(u => u.side !== this.mySide);
      return enemies.some(e =>
        Math.abs(unit.col - e.col) <= 1 && Math.abs(unit.row - e.row) <= 1 &&
        !(unit.col === e.col && unit.row === e.row) &&
        // Use cube-distance === 1 for precise hex adjacency
        (() => {
          const ax = unit.col - (unit.row - (unit.row & 1)) / 2;
          const bx = e.col   - (e.row   - (e.row   & 1)) / 2;
          return Math.max(Math.abs(ax - bx), Math.abs((-ax - unit.row) - (-bx - e.row)), Math.abs(unit.row - e.row)) === 1;
        })(),
      );
    }

    // Type filter (infantry-only, armor-only, artillery-only cards)
    if (fx?.unitTypeFilter && unit.type !== fx.unitTypeFilter) return false;

    // Multi-section cards (Pincer Move, General Advance, Recon in Force)
    if (fx?.multiSection) {
      const unitSecs = this.getUnitSections(unit.col, unit.row);
      const matchingSections = fx.multiSection.filter(s =>
        s.section === 'any' || unitSecs.includes(s.section as 'left' | 'center' | 'right'),
      );
      if (matchingSections.length === 0) return false;
      // Check border-hex aware quota — unit must have room in at least one of its sections
      const quota = this.computeAvailableQuota(fx.multiSection);
      return matchingSections.some(s => s.count === 'all' || (quota[s.section] ?? 0) > 0);
    }

    // Standard section check
    if (card.section === 'any') return true;
    return this.getUnitSections(unit.col, unit.row).includes(card.section as 'left' | 'center' | 'right');
  }

  /** Mirror of the server's computeAvailableQuota — used for accurate gold highlights. */
  private computeAvailableQuota(sections: NonNullable<import('~shared/types').CardEffects['multiSection']>): Record<string, number> {
    const quota: Record<string, number> = {};
    for (const s of sections) quota[s.section] = s.count === 'all' ? 999 : (s.count as number);

    const ordered = this.gameState.units.filter(u => u.ordered && u.side === this.mySide);

    // Pass 1: non-border units
    for (const u of ordered) {
      const uSecs = this.getUnitSections(u.col, u.row);
      const matching = sections.filter(s => s.section === 'any' || uSecs.includes(s.section as 'left' | 'center' | 'right'));
      if (matching.length === 1) quota[matching[0].section]--;
    }

    // Pass 2: border units — assign to section with most remaining quota
    for (const u of ordered) {
      const uSecs = this.getUnitSections(u.col, u.row);
      const matching = sections.filter(s => s.section === 'any' || uSecs.includes(s.section as 'left' | 'center' | 'right'));
      if (matching.length > 1) {
        const best = matching.reduce((a, b) => (quota[a.section] ?? 0) >= (quota[b.section] ?? 0) ? a : b);
        quota[best.section]--;
      }
    }

    return quota;
  }

  /** True if the selected infantry unit is on barbed-wire and eligible to remove it. */
  private isWireRemovalEligible(unitId: string): boolean {
    const unit = this.gameState.units.find(u => u.id === unitId);
    if (!unit || unit.type !== 'infantry') return false;
    if (!canBattle(unit, this.gameState.terrain, cardMoveBonusFor(unit, this.gameState.activeCard), this.artilleryBombardMaxAttacks(unit))) return false;
    return this.gameState.terrain.some(t => t.col === unit.col && t.row === unit.row && t.overlay === 'barbed-wire');
  }

  /** True if this unit can still be ordered (toggled) in the select phase. */
  private canBeOrdered(unit: Unit): boolean {
    if (!this.gameState.activeCard) return false;
    // Already-ordered units always keep their outline (green) regardless of quota;
    // they need to remain visible so the player can de-select them.
    if (unit.side === this.mySide && unit.ordered) return true;
    return this.isEligibleForCard(unit, this.gameState.activeCard);
  }

  private getHint(): string {
    const s = this.gameState;
    if (s.phase === 'ended') return '';

    if (s.phase === 'airdrop') {
      const cfg = s.pendingAirdrop;
      if (!cfg) return '';
      if (cfg.side === this.mySide)
        return `Airdrop: click any hex to set the drop zone centre (radius ${cfg.radius}). ${cfg.count} paratroopers will scatter randomly.`;
      return 'Waiting for Allied airdrop — paratroopers are being deployed…';
    }

    if (s.phase === 'commissar-priming') {
      if (s.commissarSide === this.mySide)
        return 'Commissar: select 1 Command card from your hand to commit face-down for Turn 1.';
      return 'Waiting for opponent to issue their initial standing orders…';
    }

    if (s.commissarSide === this.mySide && this.isMyTurn() && s.turnPhase === 'idle') {
      const queued = s.commissarQueuedCard;
      return queued
        ? `Commissar: select a card to commit for NEXT turn — this reveals & plays "${queued.name}" (queued last turn). Or play a Recon/Counter-Attack card directly to bypass.`
        : 'Commissar: select a card to commit for next turn.';
    }

    if (s.pendingHill317Choice) {
      return this.mySide === 'allies'
        ? 'Hill 317: choose how to play this Recon card.'
        : 'Waiting for Allied player to resolve Hill 317 Recon choice…';
    }

    if (s.pendingDraw) {
      return s.pendingDraw.side === this.mySide
        ? 'Choose 1 card to keep from the 2 drawn.'
        : 'Waiting for opponent to pick a Recon card…';
    }

    if (s.pendingRetreat) {
      if (s.pendingRetreat.defendingSide === this.mySide) {
        const n    = s.pendingRetreat.stepsRemaining;
        const dest = s.pendingRetreat.validHexes.length;
        if (dest === 0) return `No valid retreat path — losing a figure for each blocked step.`;
        if (dest === 1) return `${n} flag${n > 1 ? 's' : ''} — only 1 valid destination, confirming automatically…`;
        return `${n} flag${n > 1 ? 's' : ''} — click where the unit will end up after retreating ${n} step${n > 1 ? 's' : ''}.`;
      }
      return 'Waiting for opponent to resolve retreat…';
    }

    const opponent = s.isSinglePlayer ? 'Computer' : 'Opponent';
    if (!this.isMyTurn()) {
      switch (s.turnPhase) {
        case 'select': return `${opponent} is ordering units…`;
        case 'move':   return `${opponent} is moving units…`;
        case 'battle': return s.pendingAirStrike
          ? `${opponent} is rolling Air Strike dice — waiting for next target…`
          : `${opponent} is attacking…`;
        default:       return `Waiting for ${opponent.toLowerCase()}…`;
      }
    }

    switch (s.turnPhase) {
      case 'idle':
        return 'Play a command card from your hand below.';

      case 'select': {
        const artBombard = s.activeCard?.effects?.specialAction === 'artillery-bombard';
        if (artBombard) {
          const hasArtillery = s.units.some(u => u.side === this.mySide && u.type === 'artillery');
          if (!hasArtillery) return s.orderedCount === 0
            ? 'Artillery Bombard: no artillery — select 1 unit of your choice.'
            : 'Unit selected — press Confirm Orders.';
          return s.orderedCount === 0
            ? `Artillery Bombard: select all your artillery (${s.maxOrders} unit(s)). Each may move 3 hexes or battle twice.`
            : `${s.orderedCount}/${s.maxOrders} ordered — press Confirm Orders.`;
        }
        const armorAssault = s.activeCard?.effects?.specialAction === 'armor-assault';
        if (armorAssault) {
          const hasArmor = s.units.some(u => u.side === this.mySide && u.type === 'armor');
          if (!hasArmor) return s.orderedCount === 0
            ? 'Armor Assault: no armor — select 1 unit of your choice (normal movement and battle).'
            : 'Unit selected — press Confirm Orders.';
          return s.orderedCount === 0
            ? `Armor Assault: select all your armor (${s.maxOrders} unit(s)). Each may move 3 hexes and still battle.`
            : `${s.orderedCount}/${s.maxOrders} ordered — press Confirm Orders.`;
        }
        const infantryAssault = s.activeCard?.effects?.specialAction === 'infantry-assault';
        if (infantryAssault) {
          const hasInfantry = s.units.some(u => u.side === this.mySide && u.type === 'infantry');
          if (!hasInfantry) return s.orderedCount === 0
            ? 'Infantry Assault: no infantry — select 1 unit of your choice.'
            : 'Unit selected — press Confirm Orders.';
          return s.orderedCount === 0
            ? 'Infantry Assault: select your first infantry to lock the section. Move 2 hexes and battle, or 3 hexes without battle.'
            : `${s.orderedCount}/${s.maxOrders} ordered — press Confirm Orders.`;
        }
        const bel = s.activeCard?.effects?.specialAction === 'behind-enemy-lines';
        if (bel) {
          const hasInfantry = s.units.some(u => u.side === this.mySide && u.type === 'infantry');
          if (!hasInfantry) return s.orderedCount === 0
            ? 'Behind Enemy Lines: no infantry — select any 1 unit (normal order, no special effect).'
            : 'Unit selected — press Confirm Orders.';
          return s.orderedCount === 0
            ? 'Behind Enemy Lines: select 1 infantry. It will move 3 hexes, battle +1 die, then move 3 again.'
            : 'Infantry selected — press Confirm Orders.';
        }
        const firefight = s.activeCard?.effects?.specialAction === 'firefight';
        if (firefight) {
          const eligible = s.units.filter(u => this.canBeOrdered(u));
          if (eligible.length === 0) return 'No units out of enemy contact — End Turn to discard.';
          return s.orderedCount === 0
            ? `Firefight: select up to 4 units not adjacent to enemies (${eligible.length} eligible). No movement, +1 die.`
            : `${s.orderedCount}/${s.maxOrders} selected — add more or press Open Fire.`;
        }
        const closeAssault = s.activeCard?.effects?.specialAction === 'close-assault';
        if (closeAssault) {
          const eligible = s.units.filter(u => this.canBeOrdered(u));
          if (eligible.length === 0) return 'No infantry or armor adjacent to an enemy — End Turn to discard.';
          return s.orderedCount === 0
            ? `Close Assault: select infantry/armor adjacent to enemies (${eligible.length} eligible), then confirm.`
            : `${s.orderedCount}/${s.maxOrders} selected — press Confirm to advance to battle (+1 die, no movement).`;
        }
        const digIn = s.activeCard?.effects?.specialAction === 'dig-in';
        if (digIn) {
          return s.orderedCount === 0
            ? 'Dig-In: select up to 4 infantry units to place sandbags on their current hex.'
            : `${s.orderedCount}/${s.maxOrders} selected — press Confirm to place the sandbags.`;
        }
        const medics = s.activeCard?.effects?.specialAction === 'medics';
        if (medics) {
          return s.orderedCount === 0
            ? 'Medics & Mechanics: click a unit to select it for restoration, then press Confirm.'
            : 'Unit selected — press Confirm to roll the healing dice.';
        }
        const tfh = s.activeCard?.effects?.specialAction === 'their-finest-hour';
        if (tfh) {
          if (s.maxOrders === 0) return 'Their Finest Hour: all flags/grenades — no orders this turn. Press Confirm Orders.';
          const o = s.tfhOrders;
          const remaining: string[] = [];
          if (o?.infantry)  remaining.push(`${o.infantry} infantry`);
          if (o?.armor)     remaining.push(`${o.armor} armor`);
          if (o?.artillery) remaining.push(`${o.artillery} artillery`);
          if (o?.any)       remaining.push(`${o.any} any`);
          const rollStr = s.lastSpecialRoll ? ` [${s.lastSpecialRoll.join(' ')}]` : '';
          return remaining.length > 0
            ? `Their Finest Hour:${rollStr} — order ${remaining.join(', ')} (+1 die each).`
            : `Their Finest Hour:${rollStr} — all ordered. Press Confirm.`;
        }
        const eligible = s.units.filter(u => this.canBeOrdered(u));
        if (eligible.length === 0) return 'No units available for this card — End Turn to discard it.';
        const toggled = s.units.filter(u => u.ordered).length;
        return toggled === 0
          ? `Click units on the ${s.activeCard!.section} flank to order them (up to ${s.maxOrders}).`
          : `${toggled}/${s.maxOrders} ordered — click more or press Confirm Orders.`;
      }

      case 'move':
        if (s.pendingBelMove) {
          if (this.selectedUnitId)
            return this.validMoves.length > 0
              ? 'Behind Enemy Lines: click a green hex to move again, or click End Turn to finish.'
              : 'No moves available — click End Turn to finish.';
          return 'Behind Enemy Lines: click the unit (white outline) to move again up to 3 hexes, then End Turn.';
        }
        if (this.selectedUnitId)
          return this.validMoves.length > 0
            ? 'Click a green hex to move, or click another unit.'
            : 'No moves available for this unit.';
        return 'Click an ordered unit (white outline) to move it.  Click "Battle" when done.';

      case 'battle': {
        if (s.pendingOverrun) {
          if (this.isMyTurn()) {
            const orUnit = s.units.find(u => u.id === s.pendingOverrun!.unitId);
            const adj = s.units.filter(u => u.side !== this.mySide &&
              orUnit && Math.max(
                Math.abs((orUnit.col-(orUnit.row-(orUnit.row&1))/2)-(u.col-(u.row-(u.row&1))/2)),
                Math.abs((-(orUnit.col-(orUnit.row-(orUnit.row&1))/2)-orUnit.row)-(-(u.col-(u.row-(u.row&1))/2)-u.row)),
                Math.abs(orUnit.row - u.row)) === 1);
            return adj.length > 0
              ? 'Armor Overrun — click the orange armor unit, then select an adjacent enemy.'
              : 'Armor Overrun — click the orange armor unit to select a target, or press Skip.';
          }
          return 'Waiting for opponent to resolve Armor Overrun…';
        }
        if (s.pendingTakeGround) {
          if (s.pendingTakeGround.attackerSide === this.mySide)
            return 'Take Ground — click the green hex to advance, or press "Skip Take Ground".';
          return 'Waiting for opponent to resolve Take Ground…';
        }
        if (s.pendingMedicsRoll) return 'Medics: press "Roll Medics" to roll the healing dice.';
        const sa = this.specialAttackAction();
        if (s.pendingAirStrike && !this.rollingAnimation) {
          const rem = s.pendingAirTargets.length;
          return rem > 1
            ? `Air Strike: ${rem} targets remaining — click a highlighted target to roll for it.`
            : rem === 1
              ? 'Air Strike: 1 target remaining — click it or press "Next Target ▶".'
              : 'Air Strike result shown.';
        }
        if (s.pendingAirStrike) return 'Air Strike: rolling dice…';
        if (sa === 'barrage') return 'Barrage: click an enemy unit to fire 4 dice against it.';
        if (sa === 'air-power') {
          const n = this.airPowerTargets.length;
          const dicePerHex = (this.mySide === 'allies' || s.hill317AirStrikeSection) ? 2 : 1;
          const sectionNote = s.hill317AirStrikeSection
            ? ` · Hill 317: at least 1 target must be in the ${s.hill317AirStrikeSection.toUpperCase()} section`
            : '';
          return n === 0
            ? `Air Power: click 1–4 adjacent enemy units, then press Launch (${dicePerHex} ${dicePerHex === 1 ? 'die' : 'dice'} each, flags can't be ignored${sectionNote}).`
            : `Air Power: ${n} unit(s) selected — click more to add/remove, then press Launch Air Strike.${sectionNote}`;
        }
        if (this.selectedUnitId) {
          if (this.pendingTargetId) return 'Press "Roll Dice" to attack, or click a different target.';
          const wireEligible = this.isWireRemovalEligible(this.selectedUnitId);
          if (wireEligible && this.validTargetIds.length > 0)
            return 'Press "Remove Wire" to clear the wire, or click a red-outlined enemy to attack instead.';
          if (wireEligible)
            return 'Press "Remove Wire" to clear the barbed wire (no attack targets in range).';
          return this.validTargetIds.length > 0
            ? 'Click a red-outlined enemy to select target.'
            : 'No targets in range for this unit.';
        }
        return 'Click an eligible unit (orange outline) to battle.  Click End Turn when done.';
      }
    }
  }

  // ── Unit rendering ────────────────────────────────────────────────────

  private renderUnits(): void {
    const seen = new Set<string>();
    for (const unit of this.gameState.units) {
      seen.add(unit.id);
      let sprite = this.unitSprites.get(unit.id);
      if (!sprite) {
        sprite = new UnitSprite(this, unit, this.boardOriginX, this.boardOriginY, this.flipped, this.gameState.factions);
        sprite.on('pointerdown', () => this.onUnitClick(unit.id));
        if (this.isTouch) this.boardLayer.add(sprite);
        this.unitSprites.set(unit.id, sprite);
      } else {
        sprite.update(unit, this.boardOriginX, this.boardOriginY, this.flipped, this.gameState.factions);
      }
    }
    for (const [id, sprite] of this.unitSprites) {
      if (!seen.has(id)) { sprite.destroy(); this.unitSprites.delete(id); }
    }
    this.board.setOccupiedHexes(this.gameState.units.filter(u => u.figures > 0));
  }

  // ── Board highlights ──────────────────────────────────────────────────

  private refreshBoardHighlights(): void {
    if (this.selectedUnitId) return;
    if (this.gameState.phase === 'airdrop') return;
    if (this.gameState.phase === 'commissar-priming') return;
    this.board.clearHighlights();

    // Take Ground: highlight vacated hex and the advancing unit
    const tg = this.gameState.pendingTakeGround;
    if (tg && tg.attackerSide === this.mySide) {
      const advancer = this.gameState.units.find(u => u.id === tg.unitId);
      if (advancer) this.board.outlineHex(advancer.col, advancer.row, 0xffff00); // yellow = unit that can advance
      this.board.outlineHex(tg.vacatedHex.col, tg.vacatedHex.row, 0x00ff44);    // bright green = target hex
      return;
    }

    // Air Power sequential: highlight remaining target choices
    if (this.gameState.pendingAirStrike && !this.rollingAnimation && this.isMyTurn()) {
      for (const u of this.gameState.units) {
        if (this.gameState.pendingAirTargets.includes(u.id)) {
          this.board.outlineHex(u.col, u.row, 0xff8800); // orange = click to roll for this target
        }
      }
      return;
    }

    // Overrun: highlight the armor unit that may attack again
    const orPending = this.gameState.pendingOverrun;
    if (orPending && this.isMyTurn()) {
      const overrunUnit = this.gameState.units.find(u => u.id === orPending.unitId);
      if (overrunUnit) this.board.outlineHex(overrunUnit.col, overrunUnit.row, 0xff8800); // orange = select this unit
      return;
    }

    // Retreat takes priority over all normal highlights
    const retreat = this.gameState.pendingRetreat;
    if (retreat) {
      if (retreat.defendingSide === this.mySide) {
        const unit = this.gameState.units.find(u => u.id === retreat.unitId);
        if (unit) this.board.outlineHex(unit.col, unit.row, 0xffffff);
        for (const h of retreat.validHexes) {
          this.board.outlineHex(h.col, h.row, 0xffaa00);
        }
      } else {
        const unit = this.gameState.units.find(u => u.id === retreat.unitId);
        if (unit) {
          if (retreat.stepsRemaining === 1) {
            // 1-flag: draw arrows (destinations are adjacent, arrows are clear)
            this.board.showRetreatArrows(unit.col, unit.row, retreat.validHexes);
          } else {
            // Multi-flag: destinations may be 2+ hexes away; just outline them
            this.board.outlineHex(unit.col, unit.row, 0xffaa00);
            for (const h of retreat.validHexes) this.board.outlineHex(h.col, h.row, 0xffaa00);
          }
        }
      }
      return;
    }

    if (!this.isMyTurn()) {
      // Defender awareness: show which opponent units are ordered this turn
      for (const u of this.gameState.units) {
        if (u.side === this.mySide) continue;
        switch (this.gameState.turnPhase) {
          case 'select':
            if (u.ordered) this.board.outlineHex(u.col, u.row, 0x44ddff);  // cyan = enemy ordered
            break;
          case 'move':
            if (u.ordered && u.hexesMoved === 0) this.board.outlineHex(u.col, u.row, 0x88aaff);  // blue = enemy yet to move
            break;
          case 'battle':
            if (u.ordered && canBattle(u, this.gameState.terrain))
              this.board.outlineHex(u.col, u.row, 0xff8800);  // enemy — no card bonus applied on opponent's units
            break;
        }
      }
      return;
    }

    switch (this.gameState.turnPhase) {
      case 'select':
        // Gold = can be ordered, bright green = already ordered
        for (const u of this.gameState.units) {
          if (this.canBeOrdered(u)) {
            this.board.outlineHex(u.col, u.row, u.ordered ? 0x00ff88 : 0xffdd00);
          }
        }
        break;

      case 'move':
        // White outline = ordered & hasn't moved yet
        for (const u of this.gameState.units) {
          if (u.side === this.mySide && u.ordered && u.hexesMoved === 0) {
            this.board.outlineHex(u.col, u.row, 0xffffff);
          }
        }
        break;

      case 'battle': {
        const sa = this.specialAttackAction();
        if (sa && this.isMyTurn() && !this.gameState.pendingAirStrike) {
          // Barrage/Air Power: show enemy targets only while NOT mid-strike (suppress during sequential rolling)
          for (const u of this.gameState.units) {
            if (u.side !== this.mySide) {
              const staged = this.airPowerTargets.includes(u.id);
              this.board.outlineHex(u.col, u.row, staged ? 0xff8800 : 0xff4444);
            }
          }
        } else {
          // Normal battle: Orange outline = ordered & eligible to battle
          for (const u of this.gameState.units) {
            if (u.side === this.mySide && u.ordered && canBattle(u, this.gameState.terrain, cardMoveBonusFor(u, this.gameState.activeCard), this.artilleryBombardMaxAttacks(u))) {
              this.board.outlineHex(u.col, u.row, 0xff8800);
            }
          }
        }
        break;
      }
    }

    // Airdrop units highlighted until the airdrop side plays their first card.
    const log = this.gameState.log ?? [];
    const firstAirdrop = log.find(e => e.type === 'airdrop');
    if (firstAirdrop) {
      const hasPlayedCard = log.some(
        e => e.type === 'card' && e.side === firstAirdrop.side && e.turn >= firstAirdrop.turn,
      );
      if (!hasPlayedCard) {
        for (const u of this.gameState.units) {
          if (u.id.startsWith('airdrop-')) this.board.markAirdropHex(u.col, u.row);
        }
      }
    }
  }

  // ── Interaction ───────────────────────────────────────────────────────

  private onUnitClick(unitId: string): void {
    if (this.singleFingerPanning) return; // ignore taps that became pans
    // Terrain info mode on MOBILE only — desktop clicks always select units normally
    if (this.terrainInfoMode && this.isTouch) {
      const unit = this.gameState.units.find(u => u.id === unitId);
      if (unit) this.showTerrainInfo({ col: unit.col, row: unit.row });
      return;
    }
    if (this.gameState.phase === 'airdrop') return;
    if (this.gameState.pendingRetreat) return;
    if (!this.isMyTurn()) return;

    const unit = this.gameState.units.find(u => u.id === unitId);
    if (!unit) return;

    switch (this.gameState.turnPhase) {
      case 'select':
        // Toggle ordered status
        if (this.canBeOrdered(unit) || unit.ordered) {
          socketClient.socket.emit('order_unit', { unitId });
        }
        break;

      case 'move':
        // Select own ordered unit that hasn't moved yet
        if (unit.side === this.mySide && unit.ordered && unit.hexesMoved === 0) {
          this.selectUnit(unitId);
        }
        break;

      case 'battle': {
        // Air Power sequential: player clicks a remaining target to roll for it
        if (this.gameState.pendingAirStrike && !this.gameState.pendingRetreat && !this.rollingAnimation
            && this.gameState.pendingAirTargets.includes(unitId) && this.isMyTurn()) {
          socketClient.socket.emit('advance_air_strike', { targetId: unitId });
          break;
        }
        const sa = this.specialAttackAction();
        if (sa && !this.gameState.pendingAirStrike) {
          // Suppress all special-attack target interaction while a strike is in progress
          if (unit.side === this.mySide) break;
          if (sa === 'barrage') {
            // Barrage: click enemy → fire immediately (1 target only)
            socketClient.socket.emit('special_attack', { targetIds: [unitId] });
          } else {
            // Air Power: toggle target in/out of selection (up to 4)
            const idx = this.airPowerTargets.indexOf(unitId);
            if (idx >= 0) {
              this.airPowerTargets = this.airPowerTargets.filter(id => id !== unitId);
            } else if (this.airPowerTargets.length < 4) {
              this.airPowerTargets = [...this.airPowerTargets, unitId];
            }
            this.refreshBoardHighlights();
            this.updateUI();
          }
          break;
        }
        // Normal battle: click a valid target → stage it for rolling
        if (this.selectedUnitId && this.validTargetIds.includes(unitId)) {
          this.setPendingTarget(unitId);
          return;
        }
        // During Overrun: only the overrun armor unit can be selected
        if (this.gameState.pendingOverrun) {
          if (unit.id === this.gameState.pendingOverrun.unitId) this.selectUnit(unitId);
          break;
        }
        // Select own ordered unit that can still battle
        if (unit.side === this.mySide && unit.ordered && canBattle(unit, this.gameState.terrain, cardMoveBonusFor(unit, this.gameState.activeCard), this.artilleryBombardMaxAttacks(unit))) {
          this.selectUnit(unitId);
        }
        break;
      }
    }
  }

  private onHexClick(hex: HexCoord): void {
    // Terrain info mode on MOBILE only — desktop uses hover tooltip, clicks stay normal
    if (this.terrainInfoMode && this.isTouch) {
      this.showTerrainInfo(hex);
      return;
    }

    // Airdrop: allied player clicks to choose drop zone centre
    if (this.gameState.phase === 'airdrop') {
      if (this.isMyAirdrop()) {
        socketClient.socket.emit('drop_zone', { col: hex.col, row: hex.row });
      }
      return;
    }

    // Commissar pre-game priming: no board interaction, card hand only
    if (this.gameState.phase === 'commissar-priming') return;

    // Take Ground: attacker clicks the bright-green vacated hex
    const tg = this.gameState.pendingTakeGround;
    if (tg && tg.attackerSide === this.mySide) {
      if (hex.col === tg.vacatedHex.col && hex.row === tg.vacatedHex.row) {
        socketClient.socket.emit('take_ground');
      }
      return; // consume all clicks while take ground is pending
    }

    // Retreat: defender clicks a valid retreat hex
    const retreat = this.gameState.pendingRetreat;
    if (retreat && retreat.defendingSide === this.mySide) {
      if (retreat.validHexes.some(h => h.col === hex.col && h.row === hex.row)) {
        socketClient.socket.emit('retreat_step', { toCol: hex.col, toRow: hex.row });
        // Optimistically clear so a concurrent auto-confirm or double-click can't send a duplicate.
        this.gameState = { ...this.gameState, pendingRetreat: null };
        this.refreshBoardHighlights();
      }
      return;
    }
    if (!this.isMyTurn() || !this.selectedUnitId) {
      this.showTerrainInfo(hex); // dead click → show terrain info on mobile
      return;
    }
    if (this.gameState.turnPhase !== 'move') {
      this.showTerrainInfo(hex);
      return;
    }
    if (this.validMoves.some(h => h.col === hex.col && h.row === hex.row)) {
      socketClient.socket.emit('move_unit', { unitId: this.selectedUnitId, toCol: hex.col, toRow: hex.row });
      this.clearSelection();
    } else {
      this.showTerrainInfo(hex); // clicked non-move hex in move phase
    }
  }

  // ── Phase-transition warning dialog ─────────────────────────────────────

  /** Show a confirmation dialog before a phase transition.
   *  onContinue fires if the player dismisses the warning; onGoBack keeps them in the phase. */
  private showPhaseWarning(title: string, message: string, onContinue: () => void): void {
    const W = 1440, H = 768, D = 38;
    const objs: Phaser.GameObjects.GameObject[] = [];
    const track = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      objs.push(o);
      if (this.isTouch) this.hudLayer.add(o);
      return o;
    };
    const close = (proceed: boolean) => {
      objs.forEach(o => o.destroy());
      if (proceed) onContinue();
    };

    track(this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.55).setDepth(D).setInteractive());
    track(this.add.rectangle(W / 2, H / 2, 480, 210, 0x1a2d1a).setDepth(D + 1)
      .setStrokeStyle(1.5, 0xd4af37).setInteractive());
    track(this.add.text(W / 2, H / 2 - 68, title, {
      fontSize: '16px', color: '#d4af37', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(D + 2));
    track(this.add.text(W / 2, H / 2 - 22, message, {
      fontSize: '13px', color: '#cccccc', align: 'center', wordWrap: { width: 420 },
    }).setOrigin(0.5).setDepth(D + 2));

    const contBtn = track(this.add.rectangle(W / 2 + 105, H / 2 + 55, 180, 38, 0x664433)
      .setDepth(D + 2).setInteractive({ useHandCursor: true }));
    track(this.add.text(W / 2 + 105, H / 2 + 55, 'Continue anyway', { fontSize: '13px', color: '#ffaa88' })
      .setOrigin(0.5).setDepth(D + 3));
    contBtn.on('pointerdown', () => close(true));

    const backBtn = track(this.add.rectangle(W / 2 - 105, H / 2 + 55, 180, 38, 0x225522)
      .setDepth(D + 2).setInteractive({ useHandCursor: true }));
    track(this.add.text(W / 2 - 105, H / 2 + 55, 'Go back', { fontSize: '13px', color: '#aaffaa' })
      .setOrigin(0.5).setDepth(D + 3));
    backBtn.on('pointerdown', () => close(false));
  }

  /** Count unordered units that are still eligible to be ordered this turn,
   *  capped by the card's remaining quota (maxOrders − orderedCount). */
  private countUnorderedEligible(): number {
    const card = this.gameState.activeCard;
    if (!card) return 0;
    const s = this.gameState;
    const eligible = s.units.filter(u =>
      u.side === this.mySide && !u.ordered && this.isEligibleForCard(u, card),
    ).length;
    // Cap by remaining quota so e.g. "Attack Center (3)" with 2 ordered shows 1, not 5
    const remainingQuota = Math.max(0, s.maxOrders - s.orderedCount);
    return Math.min(eligible, remainingQuota);
  }

  /** Count Air Power candidates: enemies adjacent to the staged group not yet staged. */
  private countAirPowerCandidates(): number {
    const staged = this.airPowerTargets;
    const enemies = this.gameState.units.filter(u => u.side !== this.mySide);
    if (staged.length === 0) return enemies.length; // any enemy could be a first pick
    return enemies.filter(e => {
      if (staged.includes(e.id)) return false;
      // Adjacent to at least one staged unit (cube distance = 1)
      return staged.some(sid => {
        const s = this.gameState.units.find(u => u.id === sid);
        if (!s) return false;
        const ax = s.col - (s.row - (s.row & 1)) / 2, az = s.row;
        const bx = e.col - (e.row - (e.row & 1)) / 2, bz = e.row;
        return Math.max(Math.abs(ax - bx), Math.abs((-ax - az) - (-bx - bz)), Math.abs(az - bz)) === 1;
      });
    }).length;
  }

  /** True if warning should fire before confirming orders (unordered eligible units exist). */
  private shouldWarnBeforeConfirm(): boolean {
    const sa = this.gameState.activeCard?.effects?.specialAction;
    if (sa === 'barrage' || sa === 'air-power') return false; // no select phase for these
    return this.countUnorderedEligible() > 0;
  }

  /** True if warning should fire before done_moving (NO ordered unit has moved at all). */
  private shouldWarnBeforeMove(): boolean {
    const movedCount = this.gameState.units.filter(u =>
      u.side === this.mySide && u.ordered && u.hexesMoved > 0,
    ).length;
    if (movedCount > 0) return false; // at least one unit moved — player is aware

    // Only warn if at least one ordered unit actually HAS valid moves (not blocked)
    return this.gameState.units.some(u => {
      if (u.side !== this.mySide || !u.ordered || u.hexesMoved > 0) return false;
      const bel = this.belMoveParams(u);
      return getValidMoveHexes(u, this.gameState.units, this.gameState.terrain,
        bel.maxDist ?? this.artilleryBombardMoveRange(u), bel.ignoreRestrictions,
        this.gameState.scenarioSpecialRules?.includes('hill-movement-cost'),
        this.gameState.bridgeGroups).length > 0;
    });
  }

  /** Count ordered units that can still battle and have valid targets. */
  private countBattleReady(): number {
    return this.gameState.units.filter(u => {
      if (u.side !== this.mySide || !u.ordered || u.hasAttacked) return false;
      if (!canBattle(u, this.gameState.terrain, cardMoveBonusFor(u, this.gameState.activeCard), this.artilleryBombardMaxAttacks(u))) return false;
      return getValidAttackTargets(u, this.gameState.units, this.gameState.terrain, this.gameState.activeCard, undefined, this.gameState.riverBlocksInfantryBattle, this.gameState.scenarioSpecialRules?.includes('artillery-no-hill-los')).length > 0;
    }).length;
  }

  private onActionBtn(): void {
    if (!this.isMyTurn()) return;
    switch (this.gameState.turnPhase) {
      case 'select': {
        const eligible = this.countUnorderedEligible();
        // Normal cards: warn if unordered eligible units remain
        if (this.shouldWarnBeforeConfirm()) {
          this.showPhaseWarning(
            '⚠ Units not ordered',
            `${eligible} unit(s) still eligible to be ordered.\nOrder more or continue to move phase?`,
            () => socketClient.socket.emit('confirm_orders'),
          );
          return;
        }
        socketClient.socket.emit('confirm_orders');
        break;
      }
      case 'move': {
        if (this.shouldWarnBeforeMove()) {
          const count = this.gameState.units.filter(u =>
            u.side === this.mySide && u.ordered && u.hexesMoved === 0).length;
          this.showPhaseWarning(
            '⚠ Units haven\'t moved',
            `${count} ordered unit(s) haven\'t moved yet.\nAdvance to battle phase anyway?`,
            () => socketClient.socket.emit('done_moving'),
          );
          return;
        }
        socketClient.socket.emit('done_moving');
        break;
      }
      case 'battle':
        if (this.gameState.pendingOverrun && !this.gameState.pendingRetreat && !this.pendingTargetId) {
          this.showPhaseWarning(
            '⚠ Skip Armor Overrun?',
            'Your armor may attack again after taking ground.\nSkip the Overrun combat?',
            () => socketClient.socket.emit('skip_overrun'),
          );
          break;
        }
        if (this.gameState.pendingTakeGround && !this.gameState.pendingRetreat) {
          this.showPhaseWarning(
            '⚠ Skip Take Ground?',
            'Your unit could advance into the vacated hex.\nSkip Take Ground?',
            () => socketClient.socket.emit('skip_take_ground'),
          );
          break;
        }
        if (this.gameState.pendingMedicsRoll) {
          socketClient.socket.emit('roll_medics');
          break;
        }
        // Air Power launch — turnPhase is 'battle' so this must be here, not in 'select'
        if (this.specialAttackAction() === 'air-power' && this.airPowerTargets.length > 0
            && !this.gameState.pendingAirStrike) {
          const candidates = this.countAirPowerCandidates();
          if (candidates > 0 && this.airPowerTargets.length < 4) {
            this.showPhaseWarning(
              '⚠ Air Strike — targets remaining',
              `${this.airPowerTargets.length} target(s) selected.\n${candidates} more unit(s) adjacent to your selection could be added (up to 4).`,
              () => {
                socketClient.socket.emit('special_attack', { targetIds: this.airPowerTargets });
                this.airPowerTargets = [];
              },
            );
            return;
          }
          socketClient.socket.emit('special_attack', { targetIds: this.airPowerTargets });
          this.airPowerTargets = [];
          break;
        }
        if (this.gameState.pendingAirStrike && !this.gameState.pendingRetreat
            && this.gameState.pendingAirTargets.length === 1) {
          // Only 1 target left — button auto-selects it
          socketClient.socket.emit('advance_air_strike', { targetId: this.gameState.pendingAirTargets[0] });
          break;
        }
        if (this.selectedUnitId && this.pendingTargetId) {
          socketClient.socket.emit('attack', {
            attackerId: this.selectedUnitId,
            targetId:   this.pendingTargetId,
          });
          this.clearSelection();
        } else if (this.selectedUnitId && this.isWireRemovalEligible(this.selectedUnitId)) {
          socketClient.socket.emit('remove_wire', { unitId: this.selectedUnitId });
          this.clearSelection();
        }
        break;
    }
  }

  private setPendingTarget(targetId: string): void {
    const attacker = this.gameState.units.find(u => u.id === this.selectedUnitId)!;
    const target   = this.gameState.units.find(u => u.id === targetId)!;
    this.pendingTargetId     = targetId;
    this.pendingDiceCount    = attackDiceCount(attacker, target, this.gameState.terrain, this.gameState.activeCard);
    this.pendingDiceBreakdown = attackDiceBreakdown(attacker, target, this.gameState.terrain, this.gameState.activeCard);

    // Refresh board: red outline for all targets, orange for selected target
    this.board.clearHighlights();
    this.board.outlineHex(attacker.col, attacker.row, 0xffff00);
    for (const id of this.validTargetIds) {
      const t = this.gameState.units.find(u => u.id === id)!;
      this.board.outlineHex(t.col, t.row, id === targetId ? 0xff8800 : 0xff4444);
      this.board.showDiceHint(t.col, t.row, attackDiceCount(attacker, t, this.gameState.terrain, this.gameState.activeCard));
    }
    this.updateUI();
  }

  private selectUnit(unitId: string): void {
    if (this.selectedUnitId) this.unitSprites.get(this.selectedUnitId)?.deselect();
    this.pendingTargetId = null;
    this.selectedUnitId  = unitId;
    const unit = this.gameState.units.find(u => u.id === unitId)!;
    this.unitSprites.get(unitId)?.select();

    if (this.gameState.turnPhase === 'move') {
      const bel = this.belMoveParams(unit);
      this.validMoves      = getValidMoveHexes(unit, this.gameState.units, this.gameState.terrain, bel.maxDist ?? this.artilleryBombardMoveRange(unit), bel.ignoreRestrictions, this.gameState.scenarioSpecialRules?.includes('hill-movement-cost'), this.gameState.bridgeGroups);
      this.validTargetIds  = [];
    } else {
      this.validMoves      = [];
      const orUnit = this.gameState.pendingOverrun;
      // Overrun: armor has already attacked once (attackCount=1) — allow a second attack
      const overrunMaxAttacks = (orUnit && orUnit.unitId === unit.id) ? 2 : undefined;
      let targets = getValidAttackTargets(unit, this.gameState.units, this.gameState.terrain, this.gameState.activeCard, overrunMaxAttacks, this.gameState.riverBlocksInfantryBattle, this.gameState.scenarioSpecialRules?.includes('artillery-no-hill-los'));
      if (orUnit && orUnit.unitId === unit.id) {
        const adjacent = targets.filter(t =>
          Math.max(...[
            Math.abs((unit.col - (unit.row - (unit.row & 1)) / 2) - (t.col - (t.row - (t.row & 1)) / 2)),
            Math.abs((-(unit.col - (unit.row - (unit.row & 1)) / 2) - unit.row) - (-(t.col - (t.row - (t.row & 1)) / 2) - t.row)),
            Math.abs(unit.row - t.row),
          ]) === 1
        );
        if (adjacent.length > 0) targets = adjacent;
      }
      this.validTargetIds  = targets.map(u => u.id);
    }

    this.board.clearHighlights();
    this.board.highlightHexes(this.validMoves, 0x00ff00, 0.3);
    this.board.outlineHex(unit.col, unit.row, 0xffff00);
    for (const id of this.validTargetIds) {
      const t = this.gameState.units.find(u => u.id === id);
      if (t) {
        this.board.outlineHex(t.col, t.row, 0xff4444);
        this.board.showDiceHint(t.col, t.row, attackDiceCount(unit, t, this.gameState.terrain, this.gameState.activeCard));
      }
    }
    this.updateUI();  // redraws action button (e.g. "Remove Wire ✂" when infantry on wire)
  }

  /** Apply a freshly received game state to the UI (non-animation path). renderTerrain = true if board needs re-draw. */
  private applyNewState(state: GameState, renderTerrain: boolean): void {
    if (renderTerrain) this.board.render(state.terrain, state.specialHexes, this.flipped, state.terrainTheme);
    // Clear review overlay whenever a new state arrives
    if (this.reviewActive) {
      this.reviewActive = false;
      this.reviewBtn?.setFillStyle(0x224422);
      this.reviewBtnLabel?.setColor('#88bb88');
      this.board.clearReviewOverlay();
    }
    this.renderUnits();
    this.clearSelection();
    this.cardHand.clearInfo();
    this.refreshBoardHighlights();
    this.updateUI();
    if (state.phase === 'ended') this.showGameOver(state.winner!);

    // Auto-confirm retreat when only 1 valid destination (forced move — no real choice)
    const retreat = state.pendingRetreat;
    if (retreat && retreat.defendingSide === this.mySide && retreat.validHexes.length === 1) {
      const dest = retreat.validHexes[0];
      this.time.delayedCall(600, () => {
        // Re-check still pending (state might have changed during the delay)
        if (this.gameState.pendingRetreat?.defendingSide === this.mySide &&
            this.gameState.pendingRetreat.validHexes.length === 1) {
          socketClient.socket.emit('retreat_step', { toCol: dest.col, toRow: dest.row });
          // Optimistically clear so a concurrent manual click can't send a duplicate.
          this.gameState = { ...this.gameState, pendingRetreat: null };
          this.refreshBoardHighlights();
        }
      });
    }
  }

  private clearSelection(): void {
    if (this.selectedUnitId) this.unitSprites.get(this.selectedUnitId)?.deselect();
    this.selectedUnitId       = null;
    this.pendingTargetId      = null;
    this.pendingDiceBreakdown = null;
    this.validMoves           = [];
    this.validTargetIds       = [];
    this.airPowerTargets      = [];
    this.board.clearHighlights();
  }

  /** Canvas-space centre positions for a row of `count` dice in the right panel. */
  private dicePositions(count: number): { x: number; y: number }[] {
    const perRow = 3, gap = 8;
    const rowW   = perRow * DIE_SIZE + (perRow - 1) * gap;
    const startX = this.panelCX - rowW / 2 + DIE_SIZE / 2;
    // On touch the dice section is shifted down ~80px (score strip + card section is taller)
    const startY = this.isTouch ? 344 : 264;
    return Array.from({ length: count }, (_, i) => ({
      x: startX + (i % perRow) * (DIE_SIZE + gap),
      y: startY + Math.floor(i / perRow) * (DIE_SIZE + gap),
    }));
  }

  // ── UI update ─────────────────────────────────────────────────────────

  /** Redraws the two victory-medal tracks (mine bottom-left, opponent's top-right). */
  private updateMedalTracks(): void {
    const s = this.gameState;
    const oppSide: Side = this.mySide === 'allies' ? 'axis' : 'allies';
    const myMedals  = this.mySide === 'allies' ? s.alliesMedals : s.axisMedals;
    const oppMedals = oppSide === 'allies' ? s.alliesMedals : s.axisMedals;

    // Deck count badge
    if (s.deckCount !== undefined) {
      const n = s.deckCount;
      const color = n <= 10 ? '#ff4444' : n <= 20 ? '#ff9944' : '#cccccc';
      this.deckCountText.setText(`${n}`).setColor(color);
      this.deckCountImg.setTint(n <= 10 ? 0xff4444 : n <= 20 ? 0xff9944 : 0xffffff);
    }

    const myToWin  = this.mySide === 'allies' ? s.alliesMedalsToWin : s.axisMedalsToWin;
    const oppToWin = oppSide === 'allies' ? s.alliesMedalsToWin : s.axisMedalsToWin;

    if (this.isTouch) {
      const stars = (n: number, tot: number) => '★'.repeat(n) + '☆'.repeat(Math.max(0, tot - n));
      this.myScoreText.setText(`${this.mySide.toUpperCase()}  ${stars(myMedals, myToWin)}  ${myMedals}/${myToWin}`);
      this.oppScoreText.setText(`${oppSide.toUpperCase()}  ${stars(oppMedals, oppToWin)}  ${oppMedals}/${oppToWin}`);
      return;
    }
    drawMedalTrack(this.myMedalGfx,  MY_MEDAL_X,  MY_MEDAL_Y,  myMedals,  this.mySide,  myToWin);
    drawMedalTrack(this.oppMedalGfx, OPP_MEDAL_X, OPP_MEDAL_Y, oppMedals, oppSide, oppToWin);
  }

  private updateUI(): void {
    const s      = this.gameState;
    const myTurn = this.isMyTurn();

    // Commissar pre-game priming: simplified UI, but the card hand stays interactive
    // for the Commissar-bound side to pick its Turn 1 card.
    if (s.phase === 'commissar-priming') {
      const isMyPriming = s.commissarSide === this.mySide;
      this.turnText.setText('COMMISSAR — INITIAL ORDERS');
      this.updateMedalTracks();
      this.phaseText.setText('');
      this.hintText.setText(this.getHint());
      this.waitingOverlay.setAlpha(isMyPriming ? 0 : 0.3);
      const myHand = this.mySide === 'allies' ? s.alliesHand : s.axisHand;
      this.cardHand.update(isMyPriming ? myHand : [], isMyPriming);
      this.actionBtn.setAlpha(0);
      this.actionBtnLabel.setAlpha(0);
      this.endTurnBtn.setAlpha(0.35);
      this.endTurnLabel.setAlpha(0.35);
      this.updateRightPanel();
      this.updateLogPanel();
      return;
    }

    // Airdrop phase: simplified UI
    if (s.phase === 'airdrop') {
      this.turnText.setText("AIRDROP PHASE");
      this.updateMedalTracks();
      this.phaseText.setText(s.pendingAirdrop ? `${s.pendingAirdrop.count} paratroopers · radius ${s.pendingAirdrop.radius}` : '');
      this.hintText.setText(this.getHint());
      this.waitingOverlay.setAlpha(this.isMyAirdrop() ? 0 : 0.3);
      this.cardHand.update([], false);
      this.actionBtn.setAlpha(0);
      this.actionBtnLabel.setAlpha(0);
      this.endTurnBtn.setAlpha(0.35);
      this.endTurnLabel.setAlpha(0.35);
      this.updateRightPanel();
      this.updateLogPanel();
      return;
    }

    this.turnText.setText(
      `Turn ${s.turn}  ·  ${s.currentSide.toUpperCase()}${myTurn ? '  ◀ YOUR TURN' : ''}`,
    );
    this.updateMedalTracks();

    // Phase indicator strip
    const phaseLabel = { idle: '', select: '① SELECT', move: '② MOVE', battle: '③ BATTLE' }[s.turnPhase];
    this.phaseText.setText(
      s.activeCard
        ? `${phaseLabel}  ·  ${s.activeCard.name}  [${s.activeCard.section.toUpperCase()}]`
        : phaseLabel,
    );

    this.hintText.setText(this.getHint());
    this.waitingOverlay.setAlpha(myTurn ? 0 : 0.3);
    this.updateRightPanel();
    this.updateLogPanel();

    // Bot hand badge (single-player games only)
    if (s.isSinglePlayer && s.botHandSize !== undefined) {
      const n = s.botHandSize;
      this.botHandBadge.setText(`Computer: ${n} card${n !== 1 ? 's' : ''}`);
      // On touch the badge lives inside the card-hand strip — only show it when cards are visible
      this.botHandBadge.setVisible(this.isTouch ? this.cardHand.visible : true);
    } else {
      this.botHandBadge.setVisible(false);
    }

    // Recon draw modal
    const myDraw = s.pendingDraw?.side === this.mySide ? s.pendingDraw : null;
    if (myDraw) {
      this.showDrawModal(myDraw.cards);
    } else {
      this.hideDrawModal();
    }

    // Hill 317 choice modal
    if (s.pendingHill317Choice && this.mySide === 'allies') {
      this.showHill317Modal(s.pendingHill317Choice.section);
    } else {
      this.hideHill317Modal();
    }

    // Card hand: only interactive during idle phase on your turn, and not during retreat or pendingDraw or Hill317 choice
    const myHand = this.mySide === 'allies' ? s.alliesHand : s.axisHand;
    this.cardHand.update(myHand, myTurn && s.turnPhase === 'idle' && !s.pendingRetreat && !s.pendingDraw && !s.pendingHill317Choice);

    // Action button
    const sa = this.specialAttackAction();
    const canRoll         = myTurn && this.pendingTargetId !== null && !s.pendingRetreat && !sa && !s.pendingMedicsRoll;
    const canMedicsRoll   = myTurn && s.pendingMedicsRoll && !s.pendingRetreat;
    // "Next Target ▶" only shown when 1 target remains (player clicks targets when there are multiple)
    const canNextAirTarget  = myTurn && s.pendingAirStrike && !s.pendingRetreat && !this.rollingAnimation && s.pendingAirTargets.length <= 1;
    const canSkipTakeGround = myTurn && !!s.pendingTakeGround && !s.pendingRetreat;
    const canSkipOverrun    = myTurn && !!s.pendingOverrun && !s.pendingRetreat && !this.pendingTargetId;
    const canRemoveWire = myTurn && s.turnPhase === 'battle' && !this.pendingTargetId && !s.pendingRetreat && !sa && !s.pendingMedicsRoll
      && this.selectedUnitId !== null && this.isWireRemovalEligible(this.selectedUnitId);
    const canLaunchAir = myTurn && sa === 'air-power' && this.airPowerTargets.length > 0 && !s.pendingRetreat;
    const tfhZeroOrders = myTurn && s.activeCard?.effects?.specialAction === 'their-finest-hour' && s.maxOrders === 0;
    const actionConfig: Record<string, { label: string; visible: boolean; enabled: boolean }> = {
      idle:   { label: '',                 visible: false, enabled: false },
      select: {
        label:   s.activeCard?.effects?.specialAction === 'firefight'     ? 'Open Fire ▶'      :
                 s.activeCard?.effects?.specialAction === 'close-assault' ? 'Close Assault ▶'  :
                 s.activeCard?.effects?.specialAction === 'dig-in'        ? 'Place Sandbags ▶' :
                 s.activeCard?.effects?.specialAction === 'medics'        ? 'Apply Medics ▶'   : 'Confirm Orders ▶',
        visible: true,
        enabled: myTurn && (s.orderedCount > 0 || tfhZeroOrders),
      },
      move:   { label: 'Battle ▶', visible: myTurn && !s.pendingBelMove, enabled: myTurn && !s.pendingBelMove },
      battle: {
        label:   canSkipOverrun    ? 'Skip Overrun'     :
                 canSkipTakeGround ? 'Skip Take Ground' :
                 canMedicsRoll     ? 'Roll Medics ▶' :
                 canNextAirTarget  ? 'Next Target ▶' :
                 canLaunchAir      ? `Air Strike (${this.airPowerTargets.length}) ✈` :
                 canRemoveWire     ? 'Remove Wire ✂' : 'Roll Dice ▶',
        visible:  canRoll || canRemoveWire || canLaunchAir || canMedicsRoll || canNextAirTarget || canSkipTakeGround || canSkipOverrun,
        enabled:  canRoll || canRemoveWire || canLaunchAir || canMedicsRoll || canNextAirTarget || canSkipTakeGround || canSkipOverrun,
      },
    };
    const ac = actionConfig[s.turnPhase];
    this.actionBtnLabel.setText(ac.label);
    this.actionBtn.setAlpha(ac.visible ? (ac.enabled ? 1 : 0.35) : 0);
    this.actionBtnLabel.setAlpha(ac.visible ? (ac.enabled ? 1 : 0.35) : 0);

    // End Turn button
    const endEnabled = myTurn && this.canEndTurn() && !s.pendingRetreat;
    this.endTurnBtn.setAlpha(endEnabled ? 1 : 0.35);
    this.endTurnLabel.setAlpha(endEnabled ? 1 : 0.35);
  }

  // ── Right panel ──────────────────────────────────────────────────────

  private createLeftPanel(): void {
    const W = 234;
    const cx = PANEL_L_CX;

    // Separator on the right edge of the left panel
    const leftSeparator = this.add.rectangle(242, 384, 2, 768, 0x334433);

    // ── Game log (y 57–430) ──
    const logLabel = this.add.text(8, 57, 'GAME LOG', { fontSize: '9px', color: '#777777', fontStyle: 'bold' });

    const revH = this.isTouch ? 32 : 16;
    this.reviewBtn = this.add.rectangle(216, 57, 56, revH, 0x224422)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.toggleReview());
    if (!this.isTouch) {
      this.reviewBtn
        .on('pointerover', () => this.reviewBtn.setFillStyle(0x336633))
        .on('pointerout',  () => this.reviewBtn.setFillStyle(this.reviewActive ? 0x446644 : 0x224422));
    }
    this.reviewBtnLabel = this.add.text(216, 57, 'Review', { fontSize: '9px', color: '#88bb88' }).setOrigin(0.5);

    this.logDiv = document.createElement('div');
    this.logDiv.style.cssText = [
      `width:${W}px`, 'height:330px', 'overflow-y:auto', 'box-sizing:border-box',
      'font-family:monospace', 'font-size:11px', 'line-height:1.5',
      'color:#99bb99', 'background:#080f08', 'border:1px solid #334433',
      'border-radius:3px', 'padding:4px 6px',
    ].join(';');
    const logDom = this.add.dom(cx, 240, this.logDiv);

    // ── Chat (y 440–710) ──
    const chatSeparator = this.add.rectangle(cx, 438, W + 8, 2, 0x334433);
    const chatLabel = this.add.text(8, 448, 'CHAT', { fontSize: '9px', color: '#777777', fontStyle: 'bold' });

    this.chatHistoryDiv = document.createElement('div');
    this.chatHistoryDiv.style.cssText = [
      `width:${W}px`, 'height:200px', 'overflow-y:auto', 'box-sizing:border-box',
      'font-family:monospace', 'font-size:11px', 'line-height:1.5',
      'color:#cccccc', 'background:#080808', 'border:1px solid #334433',
      'border-radius:3px', 'padding:3px 6px',
    ].join(';');
    const chatDom = this.add.dom(cx, 564, this.chatHistoryDiv);

    if (this.pendingChatHistory.length > 0) {
      this.welcomeShown = true;
      for (const m of this.pendingChatHistory) this.appendChatMessage(m.playerName, m.side, m.text);
    }

    const chatRow = document.createElement('div');
    chatRow.style.cssText = `width:${W}px;display:flex;gap:4px;box-sizing:border-box`;

    this.chatInput = document.createElement('input');
    this.chatInput.type = 'text';
    this.chatInput.placeholder = 'Message…';
    this.chatInput.maxLength = 200;
    // iOS Safari auto-zooms inputs with font-size < 16px — use 16px on touch
    const inputFontSize = this.isTouch ? '16px' : '11px';
    const inputPadding  = this.isTouch ? '8px 6px' : '3px 6px';
    this.chatInput.style.cssText = [
      'flex:1', 'background:#101810', 'color:#cccccc', 'border:1px solid #334433',
      'border-radius:3px', 'font-family:monospace', `font-size:${inputFontSize}`,
      `padding:${inputPadding}`, 'outline:none', 'min-width:0',
    ].join(';');
    this.chatInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.sendChat();
      e.stopPropagation();
    });

    const sendBtn = document.createElement('button');
    sendBtn.textContent = 'Send';
    const sendPad = this.isTouch ? '10px 14px' : '3px 8px';
    const sendFont = this.isTouch ? '14px' : '11px';
    sendBtn.style.cssText = [
      'background:#224422', 'color:#88bb88', 'border:1px solid #334433',
      'border-radius:3px', 'font-family:monospace', `font-size:${sendFont}`,
      `padding:${sendPad}`, 'cursor:pointer', 'white-space:nowrap',
    ].join(';');
    sendBtn.addEventListener('click', () => this.sendChat());

    chatRow.appendChild(this.chatInput);
    chatRow.appendChild(sendBtn);
    const chatRowDom = this.add.dom(cx, 686, chatRow);

    // Left panel (Game Log/Chat) can't be pinned to the HUD camera — DOM
    // elements always track cameras.main. Instead it auto-hides while the
    // board is zoomed/panned (see update()/setLeftPanelVisible).
    this.leftPanelDomElements = [logDom, chatDom, chatRowDom];
    this.leftPanelPhaserObjects = [
      leftSeparator, logLabel, this.reviewBtn, this.reviewBtnLabel, chatSeparator, chatLabel,
    ];
    // Parent the Phaser-side objects onto boardLayer so hudCam (which ignores
    // boardLayer) doesn't double-render them at their unzoomed position.
    if (this.isTouch) this.boardLayer.add(this.leftPanelPhaserObjects);
  }

  private createRightPanel(): void {
    const px = this.isTouch ? 1100 : 1168;
    const T  = this.isTouch;

    this.add.rectangle(px - 4, 384, 2, 768, 0x334433); // separator line

    if (T) {
      // ── Score strip (y 84–118) ──
      const oppSide: Side = this.mySide === 'allies' ? 'axis' : 'allies';
      const myColor  = this.mySide === 'allies' ? '#7799ff' : '#ff7777';
      const oppColor = oppSide === 'allies' ? '#7799ff' : '#ff7777';
      this.oppScoreText = this.add.text(this.panelCX, 84,  '', { fontSize: '14px', color: oppColor, fontStyle: 'bold' }).setOrigin(0.5).setDepth(2);
      this.myScoreText  = this.add.text(this.panelCX, 106, '', { fontSize: '14px', color: myColor,  fontStyle: 'bold' }).setOrigin(0.5).setDepth(2);
      this.add.rectangle(this.panelCX, 120, 324, 1, 0x334433).setDepth(2);

      // ── Command card (y 122–282) ──
      this.cardPanelAccent = this.add.rectangle(this.panelCX, 130, 324, 6, 0x334433);
      this.add.text(this.panelCX, 143, 'COMMAND CARD', { fontSize: '9px', color: '#777777', fontStyle: 'bold' }).setOrigin(0.5);
      this.cardPanelBg = this.add.rectangle(this.panelCX, 208, 324, 140, 0x0d1f0d);
      this.cardPanelEmpty = this.add.text(this.panelCX, 208, 'No card in play', { fontSize: '12px', color: '#445544' }).setOrigin(0.5);
      this.cardPanelImg = this.add.image(this.panelCX - 62, 207, 'card-back')
        .setDisplaySize(76, 118).setVisible(false).setInteractive({ useHandCursor: true })
        .on('pointerdown', () => {
          if (this.cardZoomImg.visible) {
            this.cardZoomImg.setVisible(false);
            this.commissarBackdrop?.setVisible(false);
          } else {
            const card = this.gameState?.activeCard;
            if (card) {
              this.cardZoomImg.setTexture(cardTextureKey(card.id, this.mySide)).setVisible(true);
              this.commissarBackdrop?.setVisible(true);
            }
          }
        });
      this.cardZoomImg = this.add.image(1060, 350, 'card-back').setDisplaySize(200, 310).setVisible(false).setDepth(20);
      this.cardPanelName = this.add.text(this.panelCX + 32, 172, '', { fontSize: '13px', color: '#ffffff', fontStyle: 'bold', wordWrap: { width: 110 }, align: 'center' }).setOrigin(0.5);
      this.cardPanelInfo = this.add.text(this.panelCX + 32, 222, '', { fontSize: '11px', color: '#aaddaa', align: 'center' }).setOrigin(0.5);

      // ── Dice results (y 288–460) ──
      this.add.rectangle(this.panelCX, 288, 324, 2, 0x334433);
      this.dicePanelTitle = this.add.text(this.panelCX, 300, 'LAST BATTLE', { fontSize: '10px', color: '#667766', fontStyle: 'bold' }).setOrigin(0.5);
      this.diceGfx = this.add.graphics();
      this.diceResultText     = this.add.text(this.panelCX, 418, '', { fontSize: '12px', color: '#cccccc' }).setOrigin(0.5);
      this.diceEliminatedText = this.add.text(this.panelCX, 436, '', { fontSize: '12px', color: '#ffaa44', fontStyle: 'bold' }).setOrigin(0.5);
      this.diceBreakdownText  = this.add.text(this.panelCX, 454, '', { fontSize: '11px', color: '#b89050', align: 'center', lineSpacing: 4 }).setOrigin(0.5, 0);

      // ── Commissar token — top-right of COMMAND CARD header row ──
      this.createCommissarToken(1395, 143, 35);

      // ── Show Cards button (y 550) ──
      const scBtn = this.add.rectangle(this.panelCX, 554, 308, 48, 0x1a3322).setStrokeStyle(1, 0x446644).setDepth(2).setInteractive({ useHandCursor: true }).on('pointerdown', () => this.setCardHandVisible(!this.cardHand.visible));
      this.showCardsBtnLbl = this.add.text(this.panelCX, 554, '▲ Show Cards', { fontSize: '16px', color: '#aaffbb', fontStyle: 'bold' }).setOrigin(0.5).setDepth(3);

      // ── Chat + Review buttons (y 604) ──
      const chatW = 146, btnH = 46;
      const chatBtn = this.add.rectangle(this.panelCX - 80, 604, chatW, btnH, 0x1a1a2d).setStrokeStyle(1, 0x4444aa).setDepth(2).setInteractive({ useHandCursor: true }).on('pointerdown', () => this.showChatModal());
      this.add.text(this.panelCX - 80, 604, '💬 Chat', { fontSize: '15px', color: '#aabbff' }).setOrigin(0.5).setDepth(3);
      const revBtn = this.add.rectangle(this.panelCX + 80, 604, chatW, btnH, 0x1a2d1a).setStrokeStyle(1, 0x44aa44).setDepth(2).setInteractive({ useHandCursor: true }).on('pointerdown', () => this.toggleReview());
      this.add.text(this.panelCX + 80, 604, '⟲ Review', { fontSize: '15px', color: '#aaffaa' }).setOrigin(0.5).setDepth(3);
      // suppress unused-var warnings
      void scBtn; void chatBtn; void revBtn;

      this.add.rectangle(this.panelCX, 636, 324, 1, 0x334433).setDepth(2);
      return;
    }

    // ── DESKTOP layout (unchanged) ──
    this.cardPanelBg     = this.add.rectangle(PANEL_CX, 132, 256, 150, 0x0d1f0d);
    this.cardPanelAccent = this.add.rectangle(PANEL_CX, 57, 256, 6, 0x334433);

    this.add.text(PANEL_CX, 70, 'COMMAND CARD', {
      fontSize: '9px', color: '#777777', fontStyle: 'bold',
    }).setOrigin(0.5);

    this.cardPanelEmpty = this.add.text(PANEL_CX, 132, 'No card in play', {
      fontSize: '12px', color: '#445544',
    }).setOrigin(0.5);

    this.cardPanelImg = this.add.image(PANEL_CX - 68, 133, 'card-back')
      .setDisplaySize(76, 118)
      .setVisible(false)
      .setInteractive({ useHandCursor: false });

    // Hover-zoom overlay — desktop only (starts invisible)
    this.cardZoomImg = this.add.image(1060, 350, 'card-back')
      .setDisplaySize(200, 310)
      .setVisible(false)
      .setDepth(20);

    this.cardPanelImg.on('pointerover', () => {
      const card = this.gameState?.activeCard;
      if (card) {
        this.cardZoomImg.setTexture(cardTextureKey(card.id, this.mySide)).setVisible(true);
      }
    });
    this.cardPanelImg.on('pointerout', () => this.cardZoomImg.setVisible(false));

    this.cardPanelName = this.add.text(PANEL_CX + 28, 100, '', {
      fontSize: '13px', color: '#ffffff', fontStyle: 'bold', wordWrap: { width: 120 }, align: 'center',
    }).setOrigin(0.5);

    this.cardPanelInfo = this.add.text(PANEL_CX + 28, 155, '', {
      fontSize: '11px', color: '#aaddaa', align: 'center',
    }).setOrigin(0.5);

    // ── Dice results (y 215–430) ──
    this.add.rectangle(PANEL_CX, 221, 256, 2, 0x334433);
    this.dicePanelTitle = this.add.text(PANEL_CX, 233, 'LAST BATTLE', {
      fontSize: '10px', color: '#667766', fontStyle: 'bold',
    }).setOrigin(0.5);

    this.diceGfx = this.add.graphics();

    this.diceResultText     = this.add.text(PANEL_CX, 352, '', { fontSize: '12px', color: '#cccccc' }).setOrigin(0.5);
    this.diceEliminatedText = this.add.text(PANEL_CX, 372, '', { fontSize: '12px', color: '#ffaa44', fontStyle: 'bold' }).setOrigin(0.5);
    this.diceBreakdownText  = this.add.text(PANEL_CX, 394, '', {
      fontSize: '11px', color: '#b89050', align: 'center', lineSpacing: 4,
    }).setOrigin(0.5, 0);

    // ── Commissar token — top-right, below the 48px header bar ──
    this.createCommissarToken(1400, 65, 40);
  }

  /**
   * Circular commissar-portrait "token" showing the queued Command card (Soviet house
   * rule) — visible only to the Commissar-bound side, in scenarios that use it. Acts as
   * a button: hover (desktop) / tap (touch) reveals the queued card via the existing
   * card-zoom overlay, same mechanism as the active-card panel.
   */
  private createCommissarToken(x: number, y: number, size: number): void {
    const radius = size / 2;
    this.commissarTokenImg = this.add.image(x, y, 'commissar-token')
      .setDisplaySize(size, size)
      .setInteractive({ useHandCursor: true })
      .setDepth(2);
    const mask = this.make.graphics({}, false);
    mask.fillStyle(0xffffff);
    mask.fillCircle(x, y, radius);
    this.commissarTokenImg.setMask(mask.createGeometryMask());
    this.commissarTokenRing  = this.add.circle(x, y, radius).setStrokeStyle(2, 0xd4af37).setDepth(2);
    this.commissarTokenBadge = this.add.rectangle(x + radius * 0.62, y + radius * 0.62, size * 0.28, size * 0.28, 0x334433)
      .setStrokeStyle(1, 0xffffff, 0.7).setDepth(2);
    this.commissarTokenLabel = this.add.text(x, y + radius + 9, 'COMMISSAR', {
      fontSize: '9px', color: '#aabbaa', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(2);

    const showZoom = () => {
      const card = this.gameState?.commissarQueuedCard;
      if (card) {
        this.cardZoomImg.setTexture(cardTextureKey(card.id, this.mySide)).setVisible(true);
        this.commissarBackdrop?.setVisible(true);
      }
    };
    const hideZoom = () => {
      this.cardZoomImg.setVisible(false);
      this.commissarBackdrop?.setVisible(false);
    };
    if (this.isTouch) {
      // Transparent full-canvas backdrop sits at depth 19 (below card zoom at depth 20).
      // Dismiss only when the tap lands outside the card image bounds.
      this.commissarBackdrop = this.add.rectangle(720, 384, 1440, 768, 0x000000, 0)
        .setDepth(19).setVisible(false).setInteractive()
        .on('pointerdown', (pointer: Phaser.Input.Pointer) => {
          if (!this.cardZoomImg.getBounds().contains(pointer.x, pointer.y)) hideZoom();
        });
      this.commissarTokenImg.on('pointerdown', () => (this.cardZoomImg.visible ? hideZoom() : showZoom()));
    } else {
      this.commissarTokenImg.on('pointerover', showZoom);
      this.commissarTokenImg.on('pointerout', hideZoom);
    }
  }

  private updateRightPanel(): void {
    const s    = this.gameState;
    const card = s.activeCard;

    // ── Commissar token — only visible to the Commissar-bound side itself ──
    const isCommissarSide = s.commissarSide === this.mySide;
    this.commissarTokenImg.setVisible(isCommissarSide);
    this.commissarTokenRing.setVisible(isCommissarSide);
    this.commissarTokenLabel.setVisible(isCommissarSide);
    const queued = s.commissarQueuedCard;
    this.commissarTokenBadge.setVisible(isCommissarSide && !!queued);
    if (isCommissarSide && queued) {
      this.commissarTokenBadge.setFillStyle(SECTION_CLR[queued.section] ?? 0x334433);
    }

    // ── Active card ──
    const hasCard = card !== null;
    this.cardPanelEmpty.setVisible(!hasCard);
    this.cardPanelImg.setVisible(hasCard);
    this.cardPanelName.setVisible(hasCard);
    this.cardPanelInfo.setVisible(hasCard);

    if (hasCard) {
      const clr = SECTION_CLR[card!.section] ?? 0x334433;
      this.cardPanelAccent.setFillStyle(clr);
      this.cardPanelBg.setStrokeStyle(1, clr, 0.6);
      this.cardPanelImg.setTexture(cardTextureKey(card!.id, this.mySide));
      this.cardPanelName.setText(card!.name);
      const rollLine = s.lastSpecialRoll
        ? `\nRoll: [${s.lastSpecialRoll.join(' ')}]` : '';
      this.cardPanelInfo.setText(
        `[${card!.section.toUpperCase()}]` +
        `\nOrders: ${card!.unitsToOrder === 9 ? 'All armor' : card!.unitsToOrder}` +
        (s.orderedCount > 0 ? `\n${s.orderedCount}/${s.maxOrders} ordered` : '') +
        rollLine,
      );
    } else {
      this.cardPanelAccent.setFillStyle(0x334433);
      this.cardPanelBg.setStrokeStyle(0);
      this.cardZoomImg.setVisible(false);
    }

    // ── Dice results / pending preview ──
    const attack      = s.lastAttack;
    const hasPending  = this.pendingTargetId !== null;
    const hasDice     = attack !== null;
    const hasIceAttrition = s.lastIceAttrition !== null && !hasDice && !hasPending;
    const hasMinefieldTrigger = s.lastMinefieldTrigger !== null && !hasDice && !hasPending && !hasIceAttrition;
    const hasSpecial  = s.lastSpecialRoll !== null && !hasDice && !hasPending && !hasIceAttrition && !hasMinefieldTrigger;
    const specialAct  = s.activeCard?.effects?.specialAction;

    const panelTitle =
      hasPending        ? 'READY TO ROLL' :
      hasIceAttrition   ? 'ICE ATTRITION' :
      hasMinefieldTrigger ? 'MINEFIELD' :
      hasSpecial && specialAct === 'their-finest-hour' ? 'THEIR FINEST HOUR' :
      hasSpecial && (specialAct === 'medics' || s.pendingMedicsRoll || s.pendingMedicsAdvance) ? 'MEDICS ROLL' :
      'LAST BATTLE';
    this.dicePanelTitle.setColor(hasPending || hasDice || hasSpecial || hasIceAttrition || hasMinefieldTrigger ? '#aabbaa' : '#667766');
    this.dicePanelTitle.setText(panelTitle);

    if (hasPending) {
      // Blank (unrevealed) dice preview — hide roller output, draw blanks via diceGfx
      this.diceRoller.clear();
      this.diceGfx.clear();
      const count = this.pendingDiceCount;
      const pos   = this.dicePositions(count);
      for (const { x, y } of pos) drawBlankDie(this.diceGfx, x, y);

      this.diceResultText.setText(`${count} ${count === 1 ? 'die' : 'dice'} — click Roll Dice`);
      this.diceEliminatedText.setText('');
      const bd = this.pendingDiceBreakdown;
      this.diceBreakdownText.setText(bd?.hasReductions ? bd.lines.join('\n') : '');

    } else if (hasDice && this.rollingAnimation) {
      // DiceRoller owns the visuals while animating — clear static layer
      this.diceGfx.clear();
      this.diceResultText.setText('');
      this.diceEliminatedText.setText('');
      this.diceBreakdownText.setText('');

    } else if (hasDice) {
      // Static final result (shown after animation completes)
      this.diceGfx.clear();
      const { faces, diceRolled, hits, flags, flagsIgnored, flagsIgnoredReason } = attack!;
      const pos = this.dicePositions(diceRolled);
      faces.forEach((face, i) => drawDieFace(this.diceGfx, face, pos[i].x, pos[i].y));

      const misses = diceRolled - hits - flags;
      const pl = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
      this.diceResultText.setText(
        `${pl(hits, 'hit', 'hits')}  ·  ${pl(flags, 'flag', 'flags')}  ·  ${pl(misses, 'miss', 'misses')}`,
      );
      this.diceEliminatedText.setText(attack!.targetEliminated ? '★ UNIT ELIMINATED' : '');
      this.diceBreakdownText.setText(
        flagsIgnored && flagsIgnoredReason
          ? `(${pl(flagsIgnored, 'flag', 'flags')} ignored due to ${flagsIgnoredReason})`
          : '',
      );

    } else if (hasIceAttrition && s.lastIceAttrition) {
      // Ice attrition display: frozen-lake dice rolled on move/retreat/take-ground entry
      if (this.iceAttritionAnimating) {
        this.diceGfx.clear();
        this.diceResultText.setText('');
        this.diceEliminatedText.setText('');
        this.diceBreakdownText.setText('');
      } else {
        this.diceGfx.clear();
        const ia  = s.lastIceAttrition;
        const pos = this.dicePositions(ia.dice.length);
        ia.dice.forEach((face, i) => drawDieFace(this.diceGfx, face, pos[i].x, pos[i].y));
        this.diceResultText.setText(
          ia.figuresLost > 0
            ? `${ia.figuresLost} figure${ia.figuresLost === 1 ? '' : 's'} lost to thin ice`
            : 'Ice held — no losses',
        );
        this.diceEliminatedText.setText(ia.eliminated ? '★ UNIT LOST THROUGH THE ICE' : '');
        this.diceBreakdownText.setText('');
      }

    } else if (hasMinefieldTrigger && s.lastMinefieldTrigger) {
      // Minefield display: dice rolled (or decoy found) on move/take-ground entry
      if (this.minefieldTriggerAnimating) {
        this.diceGfx.clear();
        this.diceResultText.setText('');
        this.diceEliminatedText.setText('');
        this.diceBreakdownText.setText('');
      } else {
        this.diceGfx.clear();
        const mt  = s.lastMinefieldTrigger;
        const pos = this.dicePositions(mt.dice.length);
        mt.dice.forEach((face, i) => drawDieFace(this.diceGfx, face, pos[i].x, pos[i].y));
        this.diceResultText.setText(
          mt.wasDecoy
            ? 'Decoy minefield — no effect'
            : mt.figuresLost > 0
              ? `${mt.figuresLost} figure${mt.figuresLost === 1 ? '' : 's'} lost to the minefield`
              : 'Minefield revealed — no hits',
        );
        this.diceEliminatedText.setText(mt.eliminated ? '★ UNIT DESTROYED' : '');
        this.diceBreakdownText.setText('');
      }

    } else if (hasSpecial && s.lastSpecialRoll) {
      // Special roll display: TFH dice or Medics healing dice
      if (this.specialRollAnimating) {
        // DiceRoller owns visuals during animation
        this.diceGfx.clear();
        this.diceResultText.setText('');
        this.diceEliminatedText.setText('');
        this.diceBreakdownText.setText('');
      } else {
        this.diceGfx.clear();
        const faces = s.lastSpecialRoll;
        const pos   = this.dicePositions(faces.length);
        faces.forEach((face, i) => drawDieFace(this.diceGfx, face, pos[i].x, pos[i].y));
        // Summary line depends on context
        if (specialAct === 'their-finest-hour' && s.tfhOrders) {
          const o = s.tfhOrders;
          const parts: string[] = [];
          if (o.infantry)  parts.push(`${o.infantry} inf`);
          if (o.armor)     parts.push(`${o.armor} arm`);
          if (o.artillery) parts.push(`${o.artillery} art`);
          if (o.any)       parts.push(`${o.any} any`);
          this.diceResultText.setText(parts.length ? `Orders: ${parts.join(' · ')}` : 'No orders (all flags)');
        } else {
          this.diceResultText.setText('');
        }
        this.diceEliminatedText.setText('');
        this.diceBreakdownText.setText('');
      }
    } else {
      this.diceGfx.clear();
      this.diceRoller.clear();
      this.diceResultText.setText('');
      this.diceEliminatedText.setText('');
      this.diceBreakdownText.setText('');
    }
  }

  // ── Chat ─────────────────────────────────────────────────────────────

  private sendChat(): void {
    if (!this.chatInput) return;
    const text = this.chatInput.value.trim();
    if (!text) return;
    socketClient.socket.emit('chat_message', { text });
    this.chatInput.value = '';
  }

  private appendChatMessage(playerName: string, side: string, text: string): void {
    this.chatMsgs.push({ playerName, side, text });
    if (this.isTouch) {
      // Append to open chat modal if visible
      if (this.chatModalDiv) this.appendToChatModalDiv(playerName, side, text);
      return;
    }
    const color = side === 'allies' ? '#7799ff' : '#ff7777';
    const entry = document.createElement('div');
    entry.style.marginBottom = '2px';
    entry.innerHTML = `<span style="color:${color};font-weight:bold">${playerName}:</span> ${text.replace(/&/g, '&amp;').replace(/</g, '&lt;')}`;
    this.chatHistoryDiv.appendChild(entry);
    this.chatHistoryDiv.scrollTop = this.chatHistoryDiv.scrollHeight;
  }

  private showWelcomeMessage(): void {
    if (!this.chatHistoryDiv) return;
    if (this.welcomeShown) return;
    this.welcomeShown = true;

    const sender = this.mySide === 'allies' ? 'Dwight' : 'Adolf';
    const color  = this.mySide === 'allies' ? '#7799ff' : '#ff7777';
    const closing = this.mySide === 'allies'
      ? `Good luck! And let us all beseech the blessing of Almighty God upon this great and noble undertaking.`
      : `Jeder Mann muß mit fanatischer Entschlossenheit kämpfen, ohne Rücksicht auf die Folgen... und seine Pflicht bis zum Äußersten erfüllen.`;
    const body   =
      `Welcome ${this.playerName}. Are you ready to battle?\n` +
      `In the top left corner you can read scenario info.\n` +
      `Pressing the ⛰ button shows tooltips for terrain rules regarding movement and battle of your units.\n` +
      closing;

    const entry = document.createElement('div');
    entry.style.marginBottom = '4px';

    const nameSpan = document.createElement('span');
    nameSpan.style.cssText = `color:${color};font-weight:bold`;
    nameSpan.textContent = sender + ':';

    const textSpan = document.createElement('span');

    entry.appendChild(nameSpan);
    entry.appendChild(document.createTextNode(' '));
    entry.appendChild(textSpan);
    this.chatHistoryDiv.appendChild(entry);

    const esc = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    let i = 0;
    let built = '';

    const tick = () => {
      if (i >= body.length) return;
      const ch = body[i++];
      built += ch;
      textSpan.innerHTML = esc(built).replace(/\n/g, '<br>');
      this.chatHistoryDiv.scrollTop = this.chatHistoryDiv.scrollHeight;

      let delay = 28;
      if ('.!?'.includes(ch)) delay = 320;
      else if (ch === ',')    delay = 110;
      else if (ch === '\n')   delay = 420;

      setTimeout(tick, delay);
    };

    tick();
  }

  // ── Log panel ────────────────────────────────────────────────────────

  private updateLogPanel(): void {
    if (!this.logDiv) return;   // left panel not created on touch
    const log = this.gameState.log ?? [];
    if (log.length === 0) {
      this.logDiv.innerHTML = '<span style="color:#445544">No events yet.</span>';
      return;
    }

    const COLOR: Record<LogEntry['type'], string> = {
      'card':        '#d4af37',
      'order':       '#88aaff',
      'move':        '#88dd88',
      'attack':      '#ff8866',
      'eliminate':   '#ff4444',
      'retreat':     '#ffdd44',
      'wire-remove': '#44ddcc',
      'medal':       '#ffd700',
      'airdrop':     '#88eeff',
      'turn-end':    '#556655',
    };

    const html = log.map(e => {
      const side  = e.side === 'allies' ? '<span style="color:#7799ff">A</span>' : '<span style="color:#ff7777">X</span>';
      const color = COLOR[e.type];
      const text  = e.text.replace(/&/g, '&amp;').replace(/</g, '&lt;');
      return `<div style="margin-bottom:2px">[T${e.turn} ${side}] <span style="color:${color}">${text}</span></div>`;
    }).join('');

    this.logDiv.innerHTML = html;
    // Auto-scroll to bottom
    this.logDiv.scrollTop = this.logDiv.scrollHeight;
  }

  private setCardHandVisible(v: boolean): void {
    this.cardHand.setVisible(v);
    this.cardStripBg?.setVisible(v);
    if (this.showCardsBtnLbl) this.showCardsBtnLbl.setText(v ? '▼ Hide Cards' : '▲ Show Cards');
    // Badge only shows when the card strip is open (it has text only in solo mode)
    if (this.botHandBadge?.text) this.botHandBadge.setVisible(v);
  }

  private toggleReview(): void {
    this.reviewActive = !this.reviewActive;
    this.reviewBtn?.setFillStyle(this.reviewActive ? 0x446644 : 0x224422);
    this.reviewBtnLabel?.setColor(this.reviewActive ? '#aaffaa' : '#88bb88');

    if (!this.reviewActive) {
      this.board.clearReviewOverlay();
      return;
    }

    // Find the most recent completed turn of the opponent
    const log = this.gameState.log ?? [];
    const opponentSide: Side = this.mySide === 'allies' ? 'axis' : 'allies';
    const turnEndEntries = log.filter(e => e.type === 'turn-end' && e.side === opponentSide);
    if (turnEndEntries.length === 0) {
      this.reviewActive = false;
      this.reviewBtn?.setFillStyle(0x224422);
      this.reviewBtnLabel?.setColor('#88bb88');
      return;
    }

    const lastOpponentTurn = turnEndEntries[turnEndEntries.length - 1].turn;
    const entries = log.filter(e =>
      e.turn === lastOpponentTurn &&
      // The opponent's own actions, plus retreats the opponent forced on us
      // (logged under our side, but a direct visible result of their attack).
      (e.side === opponentSide || (e.type === 'retreat' && e.side === this.mySide)));
    this.board.showReviewOverlay(entries);
  }

  // ── Chat modal (touch only) ───────────────────────────────────────────

  private showChatModal(): void {
    if (this.chatModalDiv) return;
    const modal = document.createElement('div');
    modal.style.cssText = 'position:fixed;inset:0;background:#080f08;z-index:10000;display:flex;flex-direction:column;padding:16px;box-sizing:border-box;font-family:monospace';

    // Header row
    const header = document.createElement('div');
    header.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;flex-shrink:0';
    const title = document.createElement('span');
    title.textContent = 'CHAT';
    title.style.cssText = 'font-size:22px;color:#d4af37;font-weight:bold';
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕';
    closeBtn.style.cssText = 'background:#1a2d1a;border:1px solid #556655;color:#aabbaa;font-size:22px;padding:8px 20px;border-radius:4px;cursor:pointer';
    closeBtn.onclick = () => this.hideChatModal();
    header.appendChild(title);
    header.appendChild(closeBtn);
    modal.appendChild(header);

    // Chat history
    const historyDiv = document.createElement('div');
    historyDiv.style.cssText = 'flex:1;overflow-y:auto;background:#080808;border:1px solid #334433;border-radius:4px;padding:8px 10px;margin-bottom:12px;font-size:16px;line-height:1.6;color:#cccccc';
    for (const m of this.chatMsgs) this.buildChatEntry(m.playerName, m.side, m.text, historyDiv);
    historyDiv.scrollTop = historyDiv.scrollHeight;
    modal.appendChild(historyDiv);
    (modal as HTMLDivElement & { _historyDiv: HTMLDivElement })._historyDiv = historyDiv;

    // Input row
    const inputRow = document.createElement('div');
    inputRow.style.cssText = 'display:flex;gap:8px;flex-shrink:0';
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Message…';
    input.maxLength = 200;
    input.style.cssText = 'flex:1;background:#101810;color:#cccccc;border:1px solid #334433;border-radius:4px;padding:12px 10px;font-size:16px;font-family:monospace';
    const sendBtn = document.createElement('button');
    sendBtn.textContent = 'Send';
    sendBtn.style.cssText = 'background:#1a3322;border:1px solid #446644;color:#aaffbb;font-size:18px;padding:12px 20px;border-radius:4px;cursor:pointer;font-family:monospace';
    const doSend = () => {
      const text = input.value.trim();
      if (!text) return;
      socketClient.socket.emit('chat_message', { text });
      input.value = '';
    };
    input.addEventListener('keydown', e => { if (e.key === 'Enter') doSend(); });
    sendBtn.onclick = doSend;
    inputRow.appendChild(input);
    inputRow.appendChild(sendBtn);
    modal.appendChild(inputRow);

    document.body.appendChild(modal);
    this.chatModalDiv = modal;
  }

  private hideChatModal(): void {
    this.chatModalDiv?.remove();
    this.chatModalDiv = null;
  }

  private buildChatEntry(playerName: string, side: string, text: string, container: HTMLDivElement): void {
    const color = side === 'allies' ? '#7799ff' : '#ff7777';
    const entry = document.createElement('div');
    entry.style.marginBottom = '4px';
    entry.innerHTML = `<span style="color:${color};font-weight:bold">${playerName}:</span> ${text.replace(/&/g, '&amp;').replace(/</g, '&lt;')}`;
    container.appendChild(entry);
  }

  private appendToChatModalDiv(playerName: string, side: string, text: string): void {
    const modal = this.chatModalDiv as (HTMLDivElement & { _historyDiv: HTMLDivElement }) | null;
    if (!modal) return;
    this.buildChatEntry(playerName, side, text, modal._historyDiv);
    modal._historyDiv.scrollTop = modal._historyDiv.scrollHeight;
  }

  // ── Log modal (touch only) ────────────────────────────────────────────

  private showLogModal(): void {
    // Reset board camera so Phaser geometry aligns with the fixed hudCam view on touch
    if (this.isTouch) this.cameras.main.setZoom(1).setScroll(0, 0);

    const W = 1440, H = 768, D = 32;
    const PW = 1380, PH = 700;
    const panelTop = H / 2 - PH / 2;

    const objs: Phaser.GameObjects.GameObject[] = [];
    const track = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      objs.push(o);
      if (this.isTouch) this.hudLayer.add(o);
      return o;
    };

    let listContainer: Phaser.GameObjects.Container | null = null;
    let scrollY = 0, maxScroll = 0;

    const onWheel = (_p: Phaser.Input.Pointer, _over: unknown, _dx: number, dy: number) => {
      if (!listContainer || maxScroll <= 0) return;
      scrollY = Phaser.Math.Clamp(scrollY + dy * 0.5, 0, maxScroll);
      listContainer.setY(contentTop - scrollY);
    };

    const close = () => {
      this.input.off('wheel', onWheel);
      objs.forEach(o => o.destroy());
    };

    // Backdrop — absorbs pointer so board isn't accidentally interacted with
    track(this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.70)
      .setDepth(D).setInteractive().on('pointerdown', close));

    // Panel — setInteractive so backdrop pointerdown doesn't bleed through it
    track(this.add.rectangle(W / 2, H / 2, PW, PH, 0x0d1a0d)
      .setDepth(D + 1).setStrokeStyle(1.5, 0xd4af37).setInteractive());

    // Title
    track(this.add.text(W / 2, panelTop + 22, 'GAME LOG', {
      fontSize: '22px', color: '#d4af37', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(D + 2));

    // Review Last Turn button — desktop only (touch has it in the right panel)
    if (!this.isTouch) {
      const revBtnX = W / 2 + PW / 2 - 120;
      const revBtn = track(this.add.rectangle(revBtnX, panelTop + 22, 200, 38, 0x224422)
        .setDepth(D + 2).setInteractive({ useHandCursor: true }));
      track(this.add.text(revBtnX, panelTop + 22, '⟲ Review Last Turn', { fontSize: '13px', color: '#aaffaa' })
        .setOrigin(0.5).setDepth(D + 3));
      revBtn.on('pointerdown', () => { this.toggleReview(); close(); });
    }

    // Close button
    const closeBtnX = W / 2 + PW / 2 - 24;
    const closeBtn = track(this.add.rectangle(closeBtnX, panelTop + 18, 36, 36, 0x332222)
      .setDepth(D + 2).setInteractive({ useHandCursor: true }).on('pointerdown', close));
    track(this.add.text(closeBtnX, panelTop + 18, '✕', { fontSize: '20px', color: '#ff8888' })
      .setOrigin(0.5).setDepth(D + 3));
    void closeBtn;

    // Scrollable content area
    const contentTop    = panelTop + 58;
    const contentBottom = panelTop + PH - 16;
    const contentH      = contentBottom - contentTop;
    const contentW      = PW - 40;
    const contentLeft   = W / 2 - contentW / 2;

    const log = this.gameState.log ?? [];
    const COLOR: Record<string, string> = {
      'card': '#d4af37', 'order': '#88aaff', 'move': '#88dd88', 'attack': '#ff8866',
      'eliminate': '#ff4444', 'retreat': '#ffdd44', 'wire-remove': '#44ddcc',
      'medal': '#ffd700', 'airdrop': '#88eeff', 'turn-end': '#556655',
    };
    const fontSize = this.isTouch ? '16px' : '13px';
    const rowGap   = this.isTouch ? 6 : 4;

    listContainer = this.add.container(contentLeft, contentTop).setDepth(D + 2);
    objs.push(listContainer);
    if (this.isTouch) this.hudLayer.add(listContainer);

    // Clip the container to the content rectangle
    const maskShape = this.make.graphics({}, false);
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(contentLeft, contentTop, contentW, contentH);
    listContainer.setMask(maskShape.createGeometryMask());
    objs.push({ destroy: () => maskShape.destroy() } as unknown as Phaser.GameObjects.GameObject);

    let y = 8;
    if (log.length === 0) {
      listContainer.add(
        this.add.text(0, y, 'No events yet.', { fontSize, color: '#445544' }),
      );
      y += 24;
    } else {
      for (const e of log) {
        const sideChar = e.side === 'allies' ? 'A' : 'X';
        const color    = COLOR[e.type] ?? '#cccccc';
        const t = this.add.text(0, y, `[T${e.turn} ${sideChar}]  ${e.text}`, {
          fontSize, color, wordWrap: { width: contentW - 8 }, lineSpacing: 2,
        });
        listContainer.add(t);
        y += t.height + rowGap;
      }
    }

    maxScroll = Math.max(0, y - contentH);
    // Start at the most recent entry (bottom)
    scrollY = maxScroll;
    listContainer.setY(contentTop - scrollY);

    // Transparent drag surface for touch scroll (pointer drag)
    let dragStartY: number | null = null;
    let dragStartScroll = 0;
    const dragRect = track(this.add.rectangle(W / 2, contentTop + contentH / 2, PW, contentH, 0x000000, 0)
      .setDepth(D + 2).setInteractive());
    dragRect.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
      dragStartY = ptr.y;
      dragStartScroll = scrollY;
    });
    dragRect.on('pointermove', (ptr: Phaser.Input.Pointer) => {
      if (dragStartY === null || !ptr.isDown) return;
      scrollY = Phaser.Math.Clamp(dragStartScroll + (dragStartY - ptr.y), 0, maxScroll);
      listContainer!.setY(contentTop - scrollY);
    });
    dragRect.on('pointerup', () => { dragStartY = null; });

    this.input.on('wheel', onWheel);
  }

  // ── Menu / quit ───────────────────────────────────────────────────────

  private createMenuButton(): void {
    const h   = this.isTouch ? 40 : 30;
    const x   = this.isTouch ? 65 : 15;
    const w   = this.isTouch ? 120 : 70;
    const fs  = this.isTouch ? '20px' : '11px';
    const btn = this.add.rectangle(x, this.headerCY, w, h, 0x243824)
      .setDepth(2)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.showMenuOverlay());
    if (!this.isTouch) {
      btn.on('pointerover', () => btn.setFillStyle(0x3a5a3a))
         .on('pointerout',  () => btn.setFillStyle(0x243824));
    }
    this.add.text(x, this.headerCY, '☰  Menu', { fontSize: fs, color: '#aabbaa', fontStyle: 'bold' })
      .setOrigin(0.5).setDepth(3);
  }

  /** Mobile-only: pinned button that resets the board camera's zoom/scroll,
   *  re-showing the left panel (see update()'s auto-hide check). */
  private createResetViewButton(): void {
    this.resetViewBtn = this.add.rectangle(1420, 24, 36, 44, 0x243824)
      .setDepth(2)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.cameras.main.setZoom(1).setScroll(0, 0));
    this.add.text(1420, 24, '⟲', { fontSize: '20px', color: '#aabbaa' })
      .setOrigin(0.5).setDepth(3);
  }

  private terrainPopupDiv: HTMLDivElement | null = null;
  private terrainInfoTimer: Phaser.Time.TimerEvent | null = null;
  // Desktop: starts ON (hover tooltip enabled). Mobile: starts OFF (tap-to-show disabled).
  private terrainInfoMode  = false; // set correctly in create() after isTouch is known
  private terrainToggleBtn!: Phaser.GameObjects.Rectangle;
  private terrainToggleLbl!: Phaser.GameObjects.Text;

  private showTerrainInfo(hex: HexCoord): void {
    if (!this.isTouch || !this.terrainInfoMode) return;

    const terrain = this.gameState.terrain.find(t => t.col === hex.col && t.row === hex.row);
    const summary = getTerrainSummary(terrain?.type ?? 'clear', terrain?.overlay, terrain?.permanent);

    this.dismissTerrainInfo(); // clear any previous popup

    // Use a CSS-fixed DOM element: completely outside Phaser's camera/zoom/scroll.
    // Always visible at the bottom of the screen regardless of board pan or zoom.
    const div = document.createElement('div');
    div.style.cssText = [
      'position:fixed', 'bottom:16px', 'left:8px', 'right:8px',
      'background:#0d1a0d', 'border:1px solid #556655', 'border-radius:6px',
      'color:#cccccc', 'font-family:monospace', 'font-size:14px',
      'line-height:1.6', 'padding:10px 14px', 'white-space:pre-wrap',
      'z-index:9999', 'max-height:38vh', 'overflow-y:auto',
      'box-shadow:0 2px 12px rgba(0,0,0,0.7)',
    ].join(';');
    div.textContent = summary;
    document.body.appendChild(div);
    this.terrainPopupDiv = div;

    // Auto-dismiss after 4 seconds
    this.terrainInfoTimer = this.time.delayedCall(4000, () => this.dismissTerrainInfo());
  }

  private dismissTerrainInfo(): void {
    if (this.terrainPopupDiv) { this.terrainPopupDiv.remove(); this.terrainPopupDiv = null; }
    if (this.terrainInfoTimer) { this.terrainInfoTimer.remove(); this.terrainInfoTimer = null; }
  }

  private toggleTerrainInfoMode(): void {
    this.terrainInfoMode = !this.terrainInfoMode;
    const on = this.terrainInfoMode;
    this.terrainToggleBtn.setFillStyle(on ? 0x446644 : 0x243824);
    this.terrainToggleLbl.setColor(on ? '#aaffaa' : '#aabbaa');
    // Desktop: sync hover tooltip enable/disable
    if (!this.isTouch) this.board.tooltipEnabled = on;
    // Mobile: dismiss any open popup when toggling off
    if (!on) this.dismissTerrainInfo();
  }

  // Canonical display order for the rules tabs — keeps lists stable across renders.
  private static readonly TERRAIN_ORDER: TerrainType[] =
    ['clear', 'beach', 'bluff', 'hill', 'hill-forest', 'hill-village', 'forest', 'hedgerow', 'town', 'city-ruins',
     'factory-complex', 'ravine', 'trench', 'marsh', 'river', 'bridge', 'pond', 'ocean', 'frozen-lake', 'impassable-hill'];
  private static readonly OVERLAY_ORDER: OverlayType[] =
    ['barbed-wire', 'sandbags', 'hedgehog', 'bunker'];
  private static readonly UNIT_TYPE_ORDER: UnitType[] = ['infantry', 'armor', 'artillery'];

  private showScenarioInfo(): void {
    const desc = SCENARIO_DESCRIPTIONS[this.gameState.scenarioId];
    if (!desc) return;

    // Reset the board camera so this full-screen dialog's DOM content (which
    // always tracks cameras.main) lines up with the fixed hudCam-rendered panel.
    if (this.isTouch) this.cameras.main.setZoom(1).setScroll(0, 0);

    const W = 1440, H = 768, D = 32;
    const PW = 820, PH = 620;
    const panelTop  = H / 2 - PH / 2;

    const objs: Phaser.GameObjects.GameObject[] = [];
    const track = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      objs.push(o);
      if (this.isTouch) this.hudLayer.add(o);
      return o;
    };

    // Per-tab content — destroyed and rebuilt whenever the active tab changes.
    let contentObjs: Phaser.GameObjects.GameObject[] = [];
    let contentDiv: HTMLDivElement | null = null;
    let listContainer: Phaser.GameObjects.Container | null = null;
    let scrollY = 0, maxScroll = 0;

    const onWheel = (_p: Phaser.Input.Pointer, _over: unknown, _dx: number, dy: number) => {
      if (!listContainer || maxScroll <= 0) return;
      scrollY = Phaser.Math.Clamp(scrollY + dy * 0.5, 0, maxScroll);
      listContainer.setY(contentTop - scrollY);
    };

    const clearContent = () => {
      contentObjs.forEach(o => o.destroy());
      contentObjs = [];
      contentDiv?.remove();
      contentDiv = null;
      listContainer = null;
      this.input.off('wheel', onWheel);
    };
    const close = () => { clearContent(); objs.forEach(o => o.destroy()); };

    // Backdrop
    track(this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.70)
      .setDepth(D).setInteractive().on('pointerdown', close));

    // Panel
    track(this.add.rectangle(W / 2, H / 2, PW, PH, 0x0d1a0d)
      .setDepth(D + 1).setStrokeStyle(1.5, 0xd4af37).setInteractive());

    // Title
    track(this.add.text(W / 2, panelTop + 20, desc.title, {
      fontSize: '15px', color: '#d4af37', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(D + 2));

    // Close button
    const closeBtn = track(this.add.rectangle(W / 2 + PW / 2 - 20, panelTop + 18, 28, 22, 0x332222)
      .setDepth(D + 2).setInteractive({ useHandCursor: true }).on('pointerdown', close));
    track(this.add.text(W / 2 + PW / 2 - 20, panelTop + 18, '✕', { fontSize: '12px', color: '#ff8888' })
      .setOrigin(0.5).setDepth(D + 3));
    void closeBtn;

    // ── Tabs ──────────────────────────────────────────────────────────────
    type TabKey = 'scenario' | 'units' | 'terrain';
    const TABS: { key: TabKey; label: string }[] = [
      { key: 'scenario', label: 'Scenario Info' },
      { key: 'units',    label: 'Unit Rules'    },
      { key: 'terrain',  label: 'Terrain Rules' },
    ];
    const tabY = panelTop + 50;
    const tabW = 160, tabH = 26, tabGap = 10;
    const tabsTotalW = TABS.length * tabW + (TABS.length - 1) * tabGap;
    let tabX = W / 2 - tabsTotalW / 2 + tabW / 2;
    const tabButtons: { key: TabKey; rect: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text }[] = [];
    for (const t of TABS) {
      const rect = track(this.add.rectangle(tabX, tabY, tabW, tabH, 0x182818)
        .setDepth(D + 2).setStrokeStyle(1, 0x3a5a3a).setInteractive({ useHandCursor: true }));
      const label = track(this.add.text(tabX, tabY, t.label, {
        fontSize: '11px', color: '#88aa88', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(D + 3));
      rect.on('pointerdown', () => selectTab(t.key));
      tabButtons.push({ key: t.key, rect, label });
      tabX += tabW + tabGap;
    }

    // Divider under tabs
    track(this.add.rectangle(W / 2, tabY + tabH / 2 + 13, PW - 40, 1, 0x334433).setDepth(D + 2));

    // Shared content-area geometry
    const contentTop    = tabY + tabH / 2 + 24;
    const contentBottom = panelTop + PH - 16;
    const contentH      = contentBottom - contentTop;
    const contentCY     = (contentTop + contentBottom) / 2;
    const contentW      = PW - 40;
    const contentLeft   = W / 2 - contentW / 2;

    type ListRow = { header: string } | { icon: (x: number, y: number) => Phaser.GameObjects.GameObject; text: string };

    const buildScrollableList = (rows: ListRow[]) => {
      listContainer = this.add.container(contentLeft, contentTop).setDepth(D + 2);
      contentObjs.push(listContainer);
      if (this.isTouch) this.hudLayer.add(listContainer);

      // Mask the list to the content rectangle (graphics shape isn't added to the display list)
      const maskShape = this.make.graphics({}, false);
      maskShape.fillStyle(0xffffff);
      maskShape.fillRect(contentLeft, contentTop, contentW, contentH);
      listContainer.setMask(maskShape.createGeometryMask());
      contentObjs.push({ destroy: () => maskShape.destroy() } as unknown as Phaser.GameObjects.GameObject);

      const iconColW  = 56;
      const textX     = iconColW + 14;
      const textWrapW = contentW - textX - 16;
      let y = 8;
      for (const row of rows) {
        if ('header' in row) {
          const t = this.add.text(0, y, row.header, { fontSize: '12px', color: '#d4af37', fontStyle: 'bold' });
          listContainer.add(t);
          y += t.height + 10;
          continue;
        }
        const textObj = this.add.text(textX, y, row.text, {
          fontSize: '11px', color: '#cccccc', lineSpacing: 3,
          wordWrap: { width: textWrapW },
        });
        listContainer.add(textObj);

        const rowH = Math.max(textObj.height, 56) + 22;
        const icon = row.icon(iconColW / 2, y + rowH / 2 - 11);
        listContainer.add(icon);

        listContainer.add(this.add.rectangle(contentW / 2, y + rowH - 11, contentW - 16, 1, 0x2a3a2a));
        y += rowH;
      }

      maxScroll = Math.max(0, y - contentH);
      scrollY = 0;
      listContainer.setY(contentTop);
      this.input.on('wheel', onWheel);
    };

    const renderScenarioTab = () => {
      const div = document.createElement('div');
      div.style.cssText = [
        `width:${contentW - 20}px`, `height:${contentH}px`,
        'overflow-y:auto', 'box-sizing:border-box',
        'font-family:monospace', 'font-size:12px', 'line-height:1.7',
        'color:#cccccc', 'white-space:pre-wrap', 'padding:6px 10px',
      ].join(';');
      div.textContent = desc.text;
      contentDiv = div;
      contentObjs.push(this.add.dom(W / 2, contentCY, div).setDepth(D + 2));
    };

    const renderUnitsTab = () => {
      const seen = new Map<string, Unit>();
      for (const u of this.gameState.units) {
        const key = `${u.type}|${u.variant ?? ''}`;
        if (!seen.has(key)) seen.set(key, u);
      }
      const entries = [...seen.values()].sort((a, b) => {
        const ai = GameScene.UNIT_TYPE_ORDER.indexOf(a.type);
        const bi = GameScene.UNIT_TYPE_ORDER.indexOf(b.type);
        if (ai !== bi) return ai - bi;
        return (a.variant ?? '').localeCompare(b.variant ?? '');
      });

      const rows: ListRow[] = entries.map(sample => ({
        icon: (x: number, y: number) => {
          // flipped=false so col=0,row=0 maps to exactly (x, y) — see hexToPixelFlipped.
          const iconData: Unit = { ...sample, id: `icon-${sample.type}-${sample.variant ?? 'std'}`, col: 0, row: 0 };
          const sprite = new UnitSprite(this, iconData, x, y, false, this.gameState.factions);
          sprite.disableInteractive();
          return sprite;
        },
        text: getUnitRulesSummary(sample.type, sample.variant),
      }));

      buildScrollableList(rows);
    };

    const renderTerrainTab = () => {
      const baseSeen    = new Set<TerrainType>();
      const overlaySeen = new Map<OverlayType, { sampleType: TerrainType; side?: Side; permanent?: boolean }>();
      for (const t of this.gameState.terrain) {
        baseSeen.add(t.type);
        if (t.overlay && !overlaySeen.has(t.overlay)) overlaySeen.set(t.overlay, { sampleType: t.type, side: t.side, permanent: t.permanent });
      }

      const rows: ListRow[] = [{ header: 'Terrain' }];
      for (const type of GameScene.TERRAIN_ORDER) {
        if (!baseSeen.has(type)) continue;
        const rule = getTerrainRule(type);
        rows.push({
          icon: (x, y) => {
            const g = this.add.graphics();
            g.setPosition(x, y);
            drawTerrainTile(g, type, undefined, undefined, this.flipped, this.gameState?.terrainTheme);
            g.setScale(0.75);
            return g;
          },
          text: `${rule.name}\n\n${rule.description}`,
        });
      }

      if (overlaySeen.size > 0) {
        rows.push({ header: 'Overlays & Objects' });
        for (const overlay of GameScene.OVERLAY_ORDER) {
          const sample = overlaySeen.get(overlay);
          if (!sample) continue;
          const rule = getTerrainRule(overlay);
          // Permanent sandbags are scenario-flavoured fortifications (e.g. Omaha
          // Beach's "Sea Wall") — same underlying rules, but never removed.
          const isPermanentSandbags = overlay === 'sandbags' && sample.permanent;
          const name = isPermanentSandbags ? 'Sea Wall' : rule.name;
          const description = isPermanentSandbags
            ? 'Attacker rolls –1 die vs infantry and armor. Unit on this hex may ignore 1 flag. '
            + 'Unlike normal Sandbags, the Sea Wall is a permanent fortification — it is never removed, even when the occupying unit moves away or is eliminated.'
            : rule.description;
          rows.push({
            icon: (x, y) => {
              const g = this.add.graphics();
              g.setPosition(x, y);
              drawTerrainTile(g, sample.sampleType, overlay, sample.side, this.flipped, this.gameState?.terrainTheme);
              g.setScale(0.75);
              return g;
            },
            text: `${name}\n\n${description}`,
          });
        }
      }

      buildScrollableList(rows);
    };

    const selectTab = (key: TabKey) => {
      clearContent();
      for (const b of tabButtons) {
        const active = b.key === key;
        b.rect.setFillStyle(active ? 0x2a4a2a : 0x182818);
        b.label.setColor(active ? '#d4af37' : '#88aa88');
      }
      if      (key === 'scenario') renderScenarioTab();
      else if (key === 'units')    renderUnitsTab();
      else                         renderTerrainTab();
    };

    selectTab('scenario');
  }

  private showMenuOverlay(): void {
    const W = 1440, H = 768, D = 30;
    const objs: Phaser.GameObjects.GameObject[] = [];
    const track = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      objs.push(o);
      if (this.isTouch) this.hudLayer.add(o);
      return o;
    };
    const close = () => objs.forEach(o => o.destroy());

    const backdrop = track(this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.55).setDepth(D).setInteractive());

    if (this.isTouch) {
      // Touch: left-aligned menu panel, larger text for readability
      const boxW = 420, boxH = 520, boxX = boxW / 2 + 20;
      track(this.add.rectangle(boxX, H / 2, boxW, boxH, 0x0f1f0f).setDepth(D + 1)
        .setStrokeStyle(2, 0xd4af37).setInteractive());
      track(this.add.text(boxX, H / 2 - boxH / 2 + 32, 'GAME MENU', {
        fontSize: '22px', color: '#d4af37', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(D + 2));

      const btnW = 370, btnH = 58, gap = 10;
      let btnY = H / 2 - boxH / 2 + 80;

      const addBtn = (label: string, color: string, textColor: string, fn: () => void) => {
        const b = track(this.add.rectangle(boxX, btnY, btnW, btnH, parseInt(color.replace('#', '0x')), 1)
          .setDepth(D + 2).setInteractive({ useHandCursor: true })
          .on('pointerdown', fn));
        track(this.add.text(boxX, btnY, label, { fontSize: '18px', color: textColor, fontStyle: 'bold' })
          .setOrigin(0.5).setDepth(D + 3));
        btnY += btnH + gap;
        return b;
      };

      if (SCENARIO_DESCRIPTIONS[this.gameState.scenarioId]) {
        addBtn('ℹ️ Scenario Info', '#1a2d1a', '#aaffbb', () => { close(); this.showScenarioInfo(); });
      }
      addBtn('📖 Rules Arbiter', '#1a2520', '#aabbaa', () => { close(); openRulesPanel(socketClient.socket); });
      addBtn('⛰ Terrain Toggle', this.terrainInfoMode ? '#1a3322' : '#182818', this.terrainInfoMode ? '#aaffbb' : '#88aa88', () => { close(); this.toggleTerrainInfoMode(); });
      addBtn('📋 Game Log', '#1a1a2d', '#aabbff', () => { close(); this.showLogModal(); });

      addBtn('Quit to Lobby', '#1a2d44', '#aaccff', () => { close(); window.location.reload(); });
      // End Game button
      const endB = track(this.add.rectangle(boxX, btnY, btnW, btnH, 0x3a1010)
        .setDepth(D + 2).setInteractive({ useHandCursor: true })
        .on('pointerdown', () => { close(); this.showConfirmOverlay(); }));
      track(this.add.text(boxX, btnY, 'End Game', { fontSize: '18px', color: '#ff9999', fontStyle: 'bold' })
        .setOrigin(0.5).setDepth(D + 3));
      void endB;

    } else {
      // Desktop: compact menu
      track(this.add.rectangle(W / 2, H / 2, 300, 220, 0x0f1f0f).setDepth(D + 1)
        .setStrokeStyle(1.5, 0xd4af37).setInteractive());
      track(this.add.text(W / 2, H / 2 - 78, 'GAME MENU', {
        fontSize: '16px', color: '#d4af37', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(D + 2));

      const quitBtn = track(this.add.rectangle(W / 2, H / 2 - 18, 220, 42, 0x1a2d44)
        .setDepth(D + 2).setInteractive({ useHandCursor: true }));
      track(this.add.text(W / 2, H / 2 - 18, 'Quit to Lobby', { fontSize: '14px', color: '#aaccff' })
        .setOrigin(0.5).setDepth(D + 3));
      quitBtn
        .on('pointerover', () => quitBtn.setFillStyle(0x2a4d77))
        .on('pointerout',  () => quitBtn.setFillStyle(0x1a2d44))
        .on('pointerdown', () => {
          close();
          window.location.reload();
        });

      const endBtn = track(this.add.rectangle(W / 2, H / 2 + 42, 220, 42, 0x3a1010)
        .setDepth(D + 2).setInteractive({ useHandCursor: true }));
      track(this.add.text(W / 2, H / 2 + 42, 'End Game', { fontSize: '14px', color: '#ff9999' })
        .setOrigin(0.5).setDepth(D + 3));
      endBtn
        .on('pointerover', () => endBtn.setFillStyle(0x661818))
        .on('pointerout',  () => endBtn.setFillStyle(0x3a1010))
        .on('pointerdown', () => { close(); this.showConfirmOverlay(); });
    }

    backdrop.on('pointerdown', close);
  }

  private showConfirmOverlay(): void {
    const W = 1440, H = 768, D = 35;
    const objs: Phaser.GameObjects.GameObject[] = [];
    const track = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      objs.push(o);
      if (this.isTouch) this.hudLayer.add(o);
      return o;
    };
    const close = () => objs.forEach(o => o.destroy());

    track(this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.70).setDepth(D).setInteractive());
    track(this.add.rectangle(W / 2, H / 2, 460, 230, 0x1a0808).setDepth(D + 1)
      .setStrokeStyle(2, 0xcc2222).setInteractive());
    track(this.add.text(W / 2, H / 2 - 68, 'Do you really want to end the game?', {
      fontSize: '17px', color: '#ffffff', fontStyle: 'bold',
      wordWrap: { width: 400 }, align: 'center',
    }).setOrigin(0.5).setDepth(D + 2));
    if (this.isTournamentMatch) {
      track(this.add.text(W / 2, H / 2 - 30, 'This will count as a loss in the tournament — medals as of now will be recorded.', {
        fontSize: '12px', color: '#ffaaaa', wordWrap: { width: 400 }, align: 'center',
      }).setOrigin(0.5).setDepth(D + 2));
    }

    const noBtn = track(this.add.rectangle(W / 2 - 95, H / 2 + 45, 140, 44, 0x225522)
      .setDepth(D + 2).setInteractive({ useHandCursor: true }));
    track(this.add.text(W / 2 - 95, H / 2 + 45, 'NO', {
      fontSize: '18px', color: '#aaffaa', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(D + 3));
    noBtn
      .on('pointerover', () => noBtn.setFillStyle(0x44aa44))
      .on('pointerout',  () => noBtn.setFillStyle(0x225522))
      .on('pointerdown', close);

    const rageBtn = track(this.add.rectangle(W / 2 + 85, H / 2 + 45, 210, 44, 0x882200)
      .setDepth(D + 2).setInteractive({ useHandCursor: true }));
    track(this.add.text(W / 2 + 85, H / 2 + 45, 'Rage Quit it Now !!', {
      fontSize: '13px', color: '#ffdd44', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(D + 3));
    rageBtn
      .on('pointerover', () => rageBtn.setFillStyle(0xcc3300))
      .on('pointerout',  () => rageBtn.setFillStyle(0x882200))
      .on('pointerdown', () => {
        close();
        localStorage.removeItem('memoir44_rejoin');
        socketClient.socket.emit('end_game');
      });
  }

  // ── Game over ─────────────────────────────────────────────────────────

  private showGameOver(winner: Side): void {
    const W = 1440, H = 768;
    const objs: Phaser.GameObjects.GameObject[] = [];

    objs.push(this.add.rectangle(W / 2, H / 2, 520, 260, 0x000000, 0.88).setDepth(20));
    objs.push(this.add.text(W / 2, H / 2 - 75, 'GAME OVER', {
      fontSize: '42px', color: '#d4af37', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(21));
    objs.push(this.add.text(W / 2, H / 2 - 10, `${winner.toUpperCase()} FORCES WIN!`, {
      fontSize: '28px', color: winner === 'allies' ? '#7799ff' : '#ff7777',
    }).setOrigin(0.5).setDepth(21));

    const btn = this.add.rectangle(W / 2 - 120, H / 2 + 65, 220, 46, 0x336633)
      .setDepth(21)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => btn.setFillStyle(0x44aa44))
      .on('pointerout',  () => btn.setFillStyle(0x336633))
      .on('pointerdown', () => {
        localStorage.removeItem('memoir44_rejoin');
        // Full reload (rather than scene.start) guarantees a clean slate —
        // stale socket listeners and Phaser scene/board state from this game
        // can't leak into the next one started from the lobby.
        window.location.reload();
      });
    objs.push(btn);
    objs.push(this.add.text(W / 2 - 120, H / 2 + 65, 'Return to Lobby', {
      fontSize: '16px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(22));

    const replayBtn = this.add.rectangle(W / 2 + 120, H / 2 + 65, 220, 46, 0x334466)
      .setDepth(21)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => replayBtn.setFillStyle(0x4466aa))
      .on('pointerout',  () => replayBtn.setFillStyle(0x334466))
      .on('pointerdown', () => {
        const roomId = this.roomId;
        requestAnimationFrame(() => {
          this.scale.refresh();
          this.scene.start('ReplayScene', { roomId });
        });
      });
    objs.push(replayBtn);
    objs.push(this.add.text(W / 2 + 120, H / 2 + 65, 'Replay this Game', {
      fontSize: '16px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(22));

    if (this.isTouch) this.hudLayer.add(objs);
  }
}
